/**
 * PixiJS renderer: a thin retained-mode layer over sim state. Each frame `draw()` syncs sprites
 * to the state (interpolated by `alpha`) and renders; `onEvent()` maps sim events to render-only
 * reactions. Nothing here mutates the sim.
 *
 * Layers (back to front): sky · earth (one tile sprite per cell) · objects (gems, gold, sacks,
 * bonus, flame, creatures, player) · particles · fx (arrows, floats, overlays) · HUD (screen space).
 */
import { Application, Container, Graphics, Rectangle, Sprite, Texture, TilingSprite, type Text } from 'pixi.js';
import { DEFAULTS, DX, DY, moverPos, type Dir, type Goal, type SimEvent, type SimState } from '../../sim/index.ts';
import type { Settings } from '../../settings.ts';
import type { Layout } from '../layout.ts';
import { ASSETS, SPRITE_SCALE } from '../../config.ts';
import { PALETTE } from '../palette.ts';
import { Camera } from './camera.ts';
import { ParticleSystem } from './particles.ts';
import { LayeredAtlas, TEX_CELL, buildPlaceholderAtlas, makeText, type SpriteAtlas } from './textures.ts';
import { Easing, Tweens } from './tween.ts';
import { ClipPlayer, samplePhase, sampleTime, type Clip, type Pose } from './anim.ts';
import { BORER, CRAWLER, GEM_POP, MUTATION, PIP, SACK } from './clips.ts';

export interface HudInfo {
  mode: 'campaign' | 'gauntlet' | 'viewer' | 'replay';
  timeLeftMs?: number;
  banner?: string;
  subBanner?: string;
  fireFrac: number; // 0 = lantern full (a shot is ready), 1 = empty
  /** WP6 lantern economy for the meter and charge lanterns. */
  lantern?: { fill: number; gemsPerShot: number; charges: number; maxCharges: number; beamReady: boolean; chargeFill: number; gemsPerCharge: number };
  fireVariant: 'lantern' | 'doubleTap';
  /** ms since the last player input (Grandpa snores after 10 s). */
  idleMs: number;
  levelName: string;
  /** A4: Grandpa's one-shot hint line (rendered as a speech plate under the portrait) and the meter throb. */
  grandpaLine?: string;
  meterThrob?: boolean;
  /** Dev A/B: closed sack art instead of the open one. */
  closedSack?: boolean;
  dpadHeld?: Dir;
  flashTicks?: number;
}

type BustState = 'neutral' | 'browsUp' | 'wince' | 'snore' | 'partyHat' | 'delighted' | 'gobsmacked' | 'content' | 'worried';
const SNORE_AFTER_MS = 10000;
/** Crush beat: ms the killing sack sits on Pip's cell, then ms to blend back to the sim's position. */
const CRUSH_HOLD_MS = 420;
const CRUSH_BLEND_MS = 160;
const V = ASSETS.vfx;

const C = TEX_CELL;
const DIR_ROT = [0, -Math.PI / 2, 0, Math.PI / 2, Math.PI];
const BONUS_CELL_TINT = 0xffc4d8;

interface CreatureFx {
  scale: number;
  lean: number; // 0..1 lean strength toward the creature's direction
  /** Override clip (mutation strip) playing on top of the state-driven cycle. */
  clip: ClipPlayer;
}

export class PixiRenderer {
  readonly app: Application;
  /** Active atlas: real art layered over placeholders, or placeholders alone (dev toggle). */
  atlas: SpriteAtlas;
  private readonly placeholder: SpriteAtlas;
  private readonly real: SpriteAtlas | null;
  readonly tweens = new Tweens();
  readonly particles: ParticleSystem;
  readonly camera = new Camera();
  /** Frames rendered in the last second (updated once per second). */
  fps = 0;
  private frames = 0;
  private fpsT = 0;

  // Layers
  private readonly world = new Container();
  private readonly sky = new Container();
  private readonly earth = new Container();
  private readonly objects = new Container();
  private readonly fx = new Container();
  private readonly hud = new Container();
  /** Non-play screens (title, shaft) drawn instead of world + HUD. */
  private readonly screens = new Container();
  private activeScreen: Container | null = null;

  // World sprites
  private cells: Sprite[] = [];
  private cellsKey = '';
  /** One continuous earth material under the whole field (no per-cell seams); dug cells are drawn over it. */
  private earthTiling: TilingSprite | null = null;
  /** Scalloped rims where dug cells meet dirt (rebuilt when the dug bitmap changes). */
  private readonly rimG = new Graphics();
  private rimKey = '';
  /** One-shot Pip clips from events (dig / turn / fire / crushed) layered over the state-driven cycle. */
  private readonly pipClip = new ClipPlayer();
  /** Render frame delta (ms) from the last update(), used by time-driven clips. */
  private dt = 16;
  private cellTint = 0xffffff;
  private gemSprites = new Map<number, Sprite>();
  private gemSparkleTick = new Map<number, number>();
  /** A3 gem discovery (render only): gem indices revealed by the headlamp cone or an adjacent dig this level. */
  private discovered = new Set<number>();
  private readonly buriedG = new Graphics();
  private gemReveal = new Map<number, number>();
  /** Top-55% crops of the gem textures for the half-buried look (keyed by atlas name + source id). */
  private gemTops = new Map<string, Texture>();
  private goldSprites = new Map<string, Sprite>();
  private sackSprites = new Map<number, Sprite>();
  private creatureSprites = new Map<number, Sprite>();
  private readonly player: Sprite;
  private readonly lamp: Sprite;
  private readonly flame: Sprite;
  private readonly bonus: Sprite;
  private readonly bonusHalo: Sprite;
  private readonly arrow: Sprite;
  private readonly missedArrow: Sprite;
  private readonly chainT: Text;
  private readonly flash = new Graphics();
  private readonly shadowG = new Graphics();
  private readonly goldRingG = new Graphics();
  private readonly dimG = new Graphics();
  private readonly transients = new Container();
  /** Dig fade-outs (the wall settling): under the objects so they never cover Pip. */
  private readonly digFx = new Container();
  private readonly floats = new Container();
  private dimAlpha = 0;
  private bonusEndAlpha = 0;
  private arrowPulse = 0;
  private flameLastCell = '';
  private sackSquash = new Map<number, { sx: number; sy: number }>();
  /** Crush beat: the killing sack is pinned to Pip's cell for the hit-stop and squash, then blends back to the sim. */
  private crushHold: { id: number; x: number; y: number; t0: number } | null = null;
  private creatureLastPos = new Map<number, [number, number]>();
  private creatureLastKind = new Map<number, 'crawler' | 'borer'>();
  private creatureFx = new Map<number, CreatureFx>();
  private goldLastTimer = new Map<string, number>();
  private playerFx = { dy: 0, rot: 0, sx: 1, sy: 1 };
  private livesPop = 1;
  private fireFlash = 0;
  private fireShake = 0;
  private lastBig: Text | null = null;
  private closedSack = false;

  // HUD (Bible §3: sky + headframe sign, field frame, mine-floor strip, lantern)
  private readonly skyG = new Graphics();
  private readonly clouds: Sprite[] = [];
  private readonly headframe: Sprite;
  private readonly sign: Sprite;
  private readonly floorProps: Sprite[] = [];
  private readonly bust: Sprite;
  private readonly plate: Sprite;
  private readonly plateT: Text;
  private readonly scoreT: Text;
  private readonly helmets: Sprite[] = [];
  private readonly ring: Sprite;
  // Objectives (WP5): Target panel on the sign, obstacle sprites in the field.
  private readonly panel: Sprite;
  private readonly panelRows: { icon: Sprite; text: Text; row: Container }[] = [];
  private panelPulse = 1;
  private readonly rowPop = [1, 1, 1];
  private obstacleSprites = new Map<string, Sprite>();
  private pickupSprites = new Map<string, Sprite>();
  // WP6: lantern meter + charges (both variants), beam, shield bubble, turbo.
  private readonly charges: Sprite[] = [];
  private readonly beamParts: Sprite[] = [];
  private readonly bubble: Sprite;
  private meterPop = 1;
  private chargePop = 1;
  private beamGlow = 0;
  private turboTrailAt = 0;
  private bustPop = 1;
  private lastBust: BustState = 'neutral';
  private signGeom = { sx0: 0, signW: 0 };
  private panelScale = 1;
  private readonly frameG = new Graphics();
  private readonly floorG = new Graphics();
  private readonly rails: Sprite;
  private readonly cart: Sprite;
  private readonly post: Sprite;
  private readonly lanternS: Sprite;
  private readonly flameIcon: Sprite;
  private readonly dpadG = new Graphics();
  private readonly bonusT: Text;
  private readonly bannerBg = new Graphics();
  private readonly bannerT: Text;
  private readonly bannerSubT: Text;
  private readonly speechG = new Graphics();
  private readonly speechT: Text;
  private speechAlpha = 0;
  private readonly fpsT2: Text;
  private bustOverride: { state: BustState; until: number } | null = null;
  private lanternFlash = 0;
  private sceneW = 0;
  private sceneH = 0;
  private showFps = false;

  private constructor(app: Application, real: SpriteAtlas | null) {
    this.app = app;
    this.placeholder = buildPlaceholderAtlas(app.renderer);
    this.real = real;
    this.atlas = real ? new LayeredAtlas(real, this.placeholder) : this.placeholder;
    this.particles = new ParticleSystem(400, 60);

    this.world.addChild(this.sky, this.earth, this.rimG, this.digFx, this.objects, this.particles.container, this.fx);
    this.objects.sortableChildren = true;
    app.stage.addChild(this.world, this.hud, this.screens);

    const sp = (name: string): Sprite => {
      const s = new Sprite(this.atlas.get(name));
      s.anchor.set(0.5);
      return s;
    };
    this.bonusHalo = sp('glow');
    this.bonusHalo.blendMode = 'add';
    this.bonusHalo.tint = PALETTE.bonus;
    this.bonusHalo.zIndex = 0;
    this.bonus = sp('bonus');
    this.bonus.zIndex = 1;
    this.flame = sp('flame');
    this.flame.zIndex = 5;
    this.lamp = sp('glow');
    this.lamp.blendMode = 'add';
    this.lamp.tint = PALETTE.pipLamp;
    this.lamp.zIndex = 3;
    this.player = sp('pip');
    this.player.zIndex = 10;
    this.bubble = sp(ASSETS.shield[0]);
    this.bubble.zIndex = 12;
    this.bubble.visible = false;
    this.objects.addChild(this.bubble);
    for (let i = 0; i < 24; i++) {
      const b = sp(ASSETS.beam.body);
      b.zIndex = 9;
      b.visible = false;
      b.blendMode = 'add';
      this.beamParts.push(b);
      this.objects.addChild(b);
    }
    this.arrow = sp('arrow');
    this.arrow.alpha = 0.7;
    this.missedArrow = sp('arrow');
    this.missedArrow.tint = 0x777777;
    this.missedArrow.visible = false;
    this.chainT = makeText('', Math.round(C * 0.4), { bold: true, color: PALETTE.gems[1] });
    this.chainT.anchor.set(0.5, 1);
    this.objects.addChild(this.bonusHalo, this.bonus, this.flame, this.lamp, this.player);
    this.fx.addChild(this.shadowG, this.goldRingG, this.transients, this.arrow, this.missedArrow, this.chainT, this.floats, this.flash, this.dimG);

    const hs = (name: string): Sprite => {
      const sp = new Sprite(this.atlas.get(name));
      sp.anchor.set(0.5);
      return sp;
    };
    for (let i = 0; i < ASSETS.cloudCount; i++) this.clouds.push(hs(ASSETS.cloud(i)));
    this.headframe = hs(ASSETS.headframeTop('collapsed'));
    this.headframe.anchor.set(0.5, 1);
    this.sign = hs(ASSETS.headframeSign('collapsed'));
    for (const n of [ASSETS.floor.rails, ASSETS.floor.rails, ASSETS.floor.cart, ASSETS.floor.rope, ASSETS.floor.logs]) {
      const sp = hs(n);
      sp.anchor.set(0.5, 1);
      this.floorProps.push(sp);
    }
    this.bust = hs(ASSETS.grandpa.neutral);
    this.plate = hs(ASSETS.brassPlate);
    this.plateT = makeText('', 12, { bold: true, color: PALETTE.uiFrame, align: 'center' });
    this.plateT.anchor.set(0.5);
    this.scoreT = makeText('0', 20, { bold: true });
    this.scoreT.anchor.set(0, 0.5);
    for (let i = 0; i < DEFAULTS.maxSpareLives; i++) this.helmets.push(hs(ASSETS.helmetLife));
    this.ring = hs(ASSETS.timerRing(0));
    this.panel = hs(ASSETS.targetPanel);
    for (let i = 0; i < 3; i++) {
      const row = new Container();
      const icon = hs(ASSETS.goalIcon('collect'));
      const text = makeText('', 12, { bold: true, color: 0xfff1d6 });
      text.anchor.set(0, 0.5);
      row.addChild(icon, text);
      this.panelRows.push({ icon, text, row });
    }
    this.rails = hs(ASSETS.floor.rails);
    this.cart = hs(ASSETS.floor.cart);
    this.post = hs(ASSETS.floor.lanternPost);
    this.post.anchor.set(0.5, 1);
    this.lanternS = hs(ASSETS.lanternMeter(0));
    for (let i = 0; i < DEFAULTS.lanternMaxCharges; i++) this.charges.push(hs(ASSETS.lanternCharge));
    this.flameIcon = hs(ASSETS.flameIconFill(ASSETS.flameIconLevels - 1));
    this.flameIcon.zIndex = 11;
    this.objects.addChild(this.flameIcon);
    this.bonusT = makeText('', 14, { bold: true, color: PALETTE.bonus });
    this.bonusT.anchor.set(0.5, 0);
    this.bannerT = makeText('', 28, { bold: true, align: 'center' });
    this.bannerT.anchor.set(0.5);
    this.bannerSubT = makeText('', 15, { align: 'center' });
    this.bannerSubT.anchor.set(0.5);
    this.speechT = makeText('', 13, { bold: true, color: 0xfff1d0, align: 'center' });
    this.speechT.anchor.set(0.5);
    this.buriedG.zIndex = 1.5;
    this.objects.addChild(this.buriedG);
    this.fpsT2 = makeText('', 11, { color: 0x88ff88 });
    this.hud.addChild(this.skyG, ...this.clouds, this.headframe, this.floorG, this.rails, this.cart, this.post, ...this.floorProps, this.frameG, this.sign, this.scoreT, ...this.helmets, this.panel, ...this.panelRows.map((r) => r.row), this.ring, this.bust, this.plate, this.plateT, this.lanternS, ...this.charges, this.dpadG, this.bonusT, this.speechG, this.speechT, this.bannerBg, this.bannerT, this.bannerSubT, this.fpsT2);
    this.showFps = typeof location !== 'undefined' && /[?&]fps/.test(location.search);
  }

  /** Show a Pixi screen instead of the game (null returns to the game view). */
  showScreen(c: Container | null): void {
    if (this.activeScreen && this.activeScreen !== c) this.screens.removeChild(this.activeScreen);
    this.activeScreen = c;
    if (c && !this.screens.children.includes(c)) this.screens.addChild(c);
    this.world.visible = !c;
    this.hud.visible = !c;
  }

  /** Render only the active screen (no state sync). */
  drawScreen(): void {
    this.app.renderer.render(this.app.stage);
  }

  /** Dev toggle: swap between real art and placeholders at runtime. Sprites re-resolve by name next frame. */
  setArtSource(source: 'real' | 'placeholder'): void {
    this.atlas = source === 'real' && this.real ? new LayeredAtlas(this.real, this.placeholder) : this.placeholder;
    this.cellsKey = ''; // rebuild tiles
    this.rimKey = '';
  }

  /** Assign a texture by name only when it changed (cheap per-frame sync). */
  private tex(sp: Sprite, name: string): void {
    const t = this.atlas.get(name);
    if (sp.texture !== t) sp.texture = t;
  }

  static async create(canvas: HTMLCanvasElement, real: SpriteAtlas | null = null): Promise<PixiRenderer> {
    const app = new Application();
    await app.init({
      canvas,
      width: window.innerWidth,
      height: window.innerHeight,
      // Capped at 2×: flat placeholder shapes gain nothing above it and 3× phones save 2.25× the fill.
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
      background: PALETTE.background,
      antialias: true,
      autoStart: false,
      preference: 'webgl',
      powerPreference: 'high-performance',
    });
    return new PixiRenderer(app, real);
  }

  resize(w: number, h: number): void {
    this.app.renderer.resize(w, h);
  }

  /** Advance render-side systems (tweens, particles, camera shake). dt in ms. */
  update(dt: number): void {
    this.dt = dt;
    this.tweens.update(dt);
    this.particles.update(dt);
    this.camera.update(dt);
    this.frames++;
    this.fpsT += dt;
    if (this.fpsT >= 1000) {
      this.fps = Math.round((this.frames * 1000) / this.fpsT);
      this.frames = 0;
      this.fpsT = 0;
    }
  }

  /** World-space centre of a cell in texture units. */
  private cx(x: number): number {
    return (x + 0.5) * C;
  }

  private cfx(id: number): CreatureFx {
    let f = this.creatureFx.get(id);
    if (!f) {
      f = { scale: 1, lean: 0, clip: new ClipPlayer() };
      this.creatureFx.set(id, f);
    }
    return f;
  }

  private v(name: string): Texture {
    return this.atlas.get(name);
  }

  /**
   * Earth puff from the ART4 particle sheet: dirt chunks (big + small) with a tan dust cloud behind.
   * `kind` 'dust' is the cloud alone (footfalls), 'smoke' the white cloud (flame).
   */
  private puff(x: number, y: number, count: number, kind: 'dirt' | 'dust' | 'smoke', angle?: number, spread = Math.PI, speed: [number, number] = [50, 140]): void {
    if (kind === 'dirt') {
      const big = Math.ceil(count / 2);
      this.particles.emit({ x, y, texture: this.v(V.dirtBig), count: big, speed, angle, spread, gravity: 520, life: [260, 480], scale: [0.16, 0.3], rotationSpeed: 6 });
      this.particles.emit({ x, y, texture: this.v(V.dirtSmall), count: count - big + 1, speed: [speed[0] * 1.2, speed[1] * 1.3], angle, spread, gravity: 520, life: [200, 420], scale: [0.14, 0.26], rotationSpeed: 8 });
      this.particles.emit({ x, y, texture: this.v(V.dustTan), count: Math.max(1, Math.round(count / 3)), speed: [speed[0] * 0.4, speed[1] * 0.5], angle, spread: spread * 1.2, gravity: 60, life: [260, 460], scale: [0.3, 0.55], alpha: 0.8 });
      return;
    }
    this.particles.emit({ x, y, texture: this.v(kind === 'dust' ? V.dustTan : V.dustWhite), count, speed, angle, spread, gravity: kind === 'dust' ? 120 : -40, life: [220, 440], scale: [0.2, 0.4], alpha: 0.85 });
  }

  /** Sparkle burst (gems, gold, bonus): a few big four-point sparkles and small ones, no tint. */
  private sparkle(x: number, y: number, count: number, extra: Partial<Parameters<ParticleSystem['emit']>[0]> = {}): void {
    this.particles.emit({ x, y, texture: this.v(V.sparkle), count: Math.ceil(count / 2), speed: [40, 150], life: [300, 520], scale: [0.25, 0.45], rotationSpeed: 3, ...extra });
    this.particles.emit({ x, y, texture: this.v(V.sparkleSmall), count: Math.floor(count / 2), speed: [60, 200], life: [250, 450], scale: [0.18, 0.32], rotationSpeed: 4, ...extra });
  }

  /** Coin fan out of a burst sack: face/edge frames cycle so each coin spins; they bounce once on the floor. */
  private coinFan(x: number, y: number, count: number, floorY: number): void {
    this.particles.emit({
      x, y, texture: this.v(V.coin), frames: [this.v(V.coin), this.v(V.coinEdge)], frameMs: 70, count,
      speed: [140, 360], angle: -Math.PI / 2, spread: Math.PI * 0.5, gravity: 1000, life: [650, 1050], scale: [0.2, 0.3],
      rotationSpeed: 4, bounceY: floorY, bounceDamping: 0.4,
    });
  }

  /** Transient sprite driven by a clip (p = 0..1 over the clip's duration). `base` is the class scale; `flip` mirrors. */
  private spawnClip(clip: Clip, x: number, y: number, base: number, flip = 1, anchorY = 0.5): void {
    const sp = new Sprite(this.atlas.get(clip.keys[0].frame));
    sp.anchor.set(0.5, anchorY);
    this.transients.addChild(sp);
    const apply = (pose: Pose): void => {
      this.tex(sp, pose.frame);
      sp.position.set(x + pose.dx * C * flip, y + pose.dy * C);
      sp.scale.set(flip * pose.sx * base, pose.sy * base);
      sp.rotation = pose.rot * flip;
      sp.alpha = pose.alpha;
    };
    apply(samplePhase(clip, 0));
    this.tweens.add(clip.duration, (p) => apply(samplePhase(clip, p)), { ease: Easing.linear, onComplete: () => { this.transients.removeChild(sp); sp.destroy(); } });
  }

  // -------------------------------------------------------------------------
  // Events → render reactions. Every SimEvent type has a case (checked by tests/events.test.ts).

  onEvent(ev: SimEvent, state: SimState): void {
    const p = state.player;
    const px = this.cx(p.x);
    const py = this.cx(p.y);
    switch (ev.type) {
      // --- digging and steering
      case 'dig': {
        // A turn into dirt raises turnExecuted and dig on the same tick: let the skid finish, then dig.
        if (this.pipClip.clip === PIP.turn) this.pipClip.then(PIP.dig);
        else this.pipClip.play(PIP.dig);
        // Dirt bursts in the direction of travel; the wall settles behind Pip over ~3 frames.
        const ang = Math.atan2(DY[ev.dir], DX[ev.dir]);
        this.puff(this.cx(ev.x) - DX[ev.dir] * C * 0.3, this.cx(ev.y) - DY[ev.dir] * C * 0.3, 5, 'dirt', ang, 0.9, [90, 220]);
        this.spawnTransient(this.atlas.get(ASSETS.earthTile((ev.x * 7 + ev.y * 13) % ASSETS.earthTiles)), this.cx(ev.x), this.cx(ev.y), 160, (t, sp) => {
          sp.alpha = 1 - t;
          sp.scale.set(1 - 0.15 * t);
        }, this.digFx);
        break;
      }
      case 'turnQueued':
        this.arrowPulse = 1;
        this.tweens.add(200, (t) => { this.arrowPulse = 1 - t; }, { tag: this.arrow });
        break;
      case 'turnExecuted':
        // Skid frame with squash-and-snap plus a dust puff at the feet.
        this.pipClip.play(PIP.turn);
        this.puff(px, py + C * 0.3, 3, 'dust', Math.PI / 2, 1.2, [40, 100]);
        break;
      case 'turnMissed': {
        // The swipe arrow flashes dim for 100 ms so the player learns the turn expired.
        const a = this.missedArrow;
        a.visible = true;
        a.alpha = 0.8;
        a.rotation = DIR_ROT[ev.dir];
        a.position.set(px + DX[ev.dir] * C * 0.8, py + DY[ev.dir] * C * 0.8);
        this.tweens.cancel(a);
        this.tweens.add(100, (t) => { a.alpha = 0.8 * (1 - t); }, { ease: Easing.linear, tag: a, onComplete: () => { a.visible = false; } });
        break;
      }
      case 'stop':
        this.puff(px - DX[p.facing] * C * 0.2, py + C * 0.3, 3, 'dust', Math.atan2(0, -DX[p.facing] || 1), 0.8, [60, 140]);
        break;

      // --- gems
      case 'gem': {
        const x = this.cx(ev.x);
        const y = this.cx(ev.y);
        // Collect pop: the gem swells and fades, sparkles fly, Grandpa warms up on a long chain.
        const gname = `gem.${(state.gemTypes[ev.y * state.width + ev.x] ?? 0) % PALETTE.gems.length}`;
        this.spawnClip({ ...GEM_POP, keys: GEM_POP.keys.map((k) => ({ ...k, frame: gname })) }, x, y, SPRITE_SCALE.gem);
        this.sparkle(x, y, 5, { speed: [50, 170], gravity: 200 });
        this.floatText('+25', x, y - C * 0.2, 0xffffff, 0.34);
        if (ev.chain >= 4) this.setBust('content', 700);
        break;
      }
      case 'octave':
        this.floatText('OCTAVE +250', px, py - C * 0.5, PALETTE.gems[1], 0.6);
        this.particles.emit({ x: px, y: py, texture: this.v(V.starWhite), count: 8, speed: [80, 260], life: [400, 800], scale: [0.25, 0.45], rotationSpeed: 6 });
        this.particles.emit({ x: px, y: py, texture: this.v(V.starPink), count: 6, speed: [80, 260], life: [400, 800], scale: [0.22, 0.4], rotationSpeed: 6 });
        this.sparkle(px, py, 6);
        this.setBust('delighted', 1300);
        break;

      // --- sacks
      case 'wobbleStart':
        this.puff(this.cx(ev.x), this.cx(ev.y) + C * 0.45, 3, 'dirt', Math.PI / 2, 0.6, [20, 60]);
        this.setBust('browsUp', 700);
        break;
      case 'sackFall':
        this.puff(this.cx(ev.x), this.cx(ev.y) - C * 0.2, 4, 'dirt', -Math.PI / 2, 1.0, [40, 110]);
        break;
      case 'sackLand': {
        const sq = { sx: 1.35, sy: 0.6 };
        const sack = state.sacks.find((k) => k.x === ev.x && k.y === ev.y);
        if (sack) {
          this.sackSquash.set(sack.id, sq);
          this.tweens.add(380, (t) => { sq.sx = 1.35 + (1 - 1.35) * t; sq.sy = 0.6 + (1 - 0.6) * t; }, { ease: Easing.outElastic, onComplete: () => this.sackSquash.delete(sack.id) });
        }
        this.camera.shake(Math.min(16, 3 + 4 * ev.rows), 220);
        this.puff(this.cx(ev.x), this.cx(ev.y) + C * 0.35, 5 + 2 * ev.rows, 'dirt', -Math.PI / 2, Math.PI * 0.8, [60, 160]);
        break;
      }
      case 'sackBreak': {
        // Burst: the torn sack swells away, coins fan up and bounce, dust settles; Grandpa is gobsmacked.
        const bx = this.cx(ev.x);
        const by = this.cx(ev.y);
        this.spawnClip(SACK.burst, bx, by + C * 0.47, SPRITE_SCALE.sack, 1, 0.97);
        this.coinFan(bx, by, 16, by + C * 0.42);
        this.puff(bx, by + C * 0.3, 4, 'dust', -Math.PI / 2, Math.PI * 0.9, [30, 90]);
        this.sparkle(bx, by, 4);
        this.setBust('gobsmacked', 900);
        break;
      }
      case 'sackShadow':
        this.camera.shake(2, 120);
        this.setBust('worried', 600);
        break;
      case 'sackPush': {
        // Heavy lean in the push direction, dirt kicked up behind.
        for (let i = 0; i < ev.count; i++) {
          const sack = state.sacks.find((k) => k.x === ev.x + ev.dx * (i + 1) && k.y === ev.y);
          if (!sack) continue;
          const sq = { sx: 0.86, sy: 1.1 };
          this.sackSquash.set(sack.id, sq);
          this.tweens.add(260, (t) => { sq.sx = 0.86 + 0.14 * t; sq.sy = 1.1 - 0.1 * t; }, { ease: Easing.outBack, onComplete: () => this.sackSquash.delete(sack.id) });
        }
        this.puff(this.cx(ev.x), this.cx(ev.y) + C * 0.4, 3, 'dirt', Math.atan2(0, -ev.dx), 0.6, [40, 110]);
        break;
      }
      case 'pushBlocked': {
        const sack = state.sacks.find((k) => k.x === ev.x && k.y === ev.y);
        if (sack) {
          const sq = { sx: 1.12, sy: 0.9 };
          this.sackSquash.set(sack.id, sq);
          this.tweens.add(180, (t) => { sq.sx = 1.12 - 0.12 * t; sq.sy = 0.9 + 0.1 * t; }, { onComplete: () => this.sackSquash.delete(sack.id) });
        }
        this.camera.shake(3, 100);
        break;
      }
      case 'gold':
        this.floatText('+500', this.cx(ev.x), this.cx(ev.y) - C * 0.3, PALETTE.gold, 0.5);
        this.coinFan(this.cx(ev.x), this.cx(ev.y), 6, this.cx(ev.y) + C * 0.42);
        this.sparkle(this.cx(ev.x), this.cx(ev.y), 6);
        this.setBust('delighted', 900);
        break;

      // --- flame
      case 'fire':
        this.pipClip.play(ev.dir === 1 ? PIP.fireUp : ev.dir === 3 ? PIP.fireDown : PIP.fire);
        this.lanternFlash = 1;
        this.tweens.add(220, (t) => { this.lanternFlash = 1 - t; }, { tag: this.lanternS });
        this.particles.emit({ x: px + DX[ev.dir] * C * 0.4, y: py + DY[ev.dir] * C * 0.4, texture: this.v(V.dustWhite), count: 5, speed: [80, 200], angle: Math.atan2(DY[ev.dir], DX[ev.dir]), spread: 0.5, life: [150, 300], scale: [0.25, 0.45], tint: [PALETTE.flame, PALETTE.flameCore] });
        this.sparkle(px + DX[ev.dir] * C * 0.5, py + DY[ev.dir] * C * 0.5, 3, { speed: [40, 120] });
        break;
      case 'flameEnd':
        this.puff(this.cx(ev.x) + DX[ev.dir] * C * 0.3, this.cx(ev.y) + DY[ev.dir] * C * 0.3, 6, 'smoke', undefined, Math.PI, [40, 140]);
        break;
      case 'fireBlocked':
        this.fireShake = 1;
        this.tweens.cancel(this.lanternS);
        this.tweens.add(220, (t) => { this.fireShake = 1 - t; }, { ease: Easing.linear, tag: this.lanternS });
        break;
      case 'flameReady':
        this.fireFlash = 1;
        this.tweens.add(350, (t) => { this.fireFlash = 1 - t; }, { tag: this.flameIcon });
        break;

      // --- creatures
      case 'creatureSpawn': {
        const f = this.cfx(ev.id);
        f.scale = 0.15;
        this.tweens.add(320, (t) => { f.scale = 0.15 + 0.85 * t; }, { ease: Easing.outBack, tag: f });
        this.puff(this.cx(ev.x), this.cx(ev.y), 5, 'dirt');
        if (ev.last) this.sparkle(this.cx(ev.x), this.cx(ev.y), 4);
        break;
      }
      case 'creatureTurn': {
        // 1-tick lean toward the new direction so the AI reads as intent.
        const f = this.cfx(ev.id);
        f.lean = 1;
        this.tweens.add(180, (t) => { f.lean = 1 - t; }, { ease: Easing.linear, tag: f });
        break;
      }
      case 'mutate': {
        // The five-stage strip plays over the creature (forward to Borer, reversed to revert), with a swell.
        const f = this.cfx(ev.id);
        f.clip.play(ev.kind === 'borer' ? MUTATION.toBorer : MUTATION.toCrawler);
        const pos = this.creatureLastPos.get(ev.id) ?? [px, py];
        const hot = ev.kind === 'borer';
        this.particles.emit({ x: pos[0], y: pos[1], texture: this.v(hot ? V.starPink : V.starWhite), count: 8, speed: [90, 240], life: [350, 650], scale: [0.2, 0.4], rotationSpeed: 8 });
        this.puff(pos[0], pos[1] + C * 0.2, 4, hot ? 'smoke' : 'dust', -Math.PI / 2, Math.PI * 0.8, [40, 120]);
        this.camera.shake(hot ? 4 : 2, 120);
        break;
      }
      case 'creatureKill': {
        const pos = this.creatureLastPos.get(ev.id) ?? [px, py];
        const [x, y] = pos;
        const kind = this.creatureLastKind.get(ev.id) ?? 'crawler';
        const clips = kind === 'borer' ? BORER : CRAWLER;
        const flip = this.creatureSprites.get(ev.id)?.scale.x ?? 1;
        if (ev.by === 'sack') {
          this.setBust('wince', 800);
          this.camera.hitStop(90);
          this.camera.shake(12, 240);
          this.spawnClip(clips.flatten, x, y, SPRITE_SCALE.creature, flip < 0 ? -1 : 1);
          this.particles.emit({ x, y, texture: this.v(V.starWhite), count: 6, speed: [80, 220], angle: -Math.PI / 2, spread: Math.PI * 0.7, gravity: 500, life: [400, 700], scale: [0.22, 0.4], rotationSpeed: 6 });
          this.puff(x, y + C * 0.3, 4, 'dust', -Math.PI / 2, Math.PI * 0.9, [40, 120]);
          this.floatText(`+${ev.points}`, x, y - C * 0.3, 0xffffff, 0.5);
        } else if (ev.by === 'flame' || ev.by === 'beam') {
          this.spawnClip(clips.flamed, x, y, SPRITE_SCALE.creature, flip < 0 ? -1 : 1);
          this.puff(x, y, 6, 'smoke', -Math.PI / 2, Math.PI, [60, 160]);
          this.floatText(`+${ev.points}`, x, y - C * 0.3, 0xffffff, 0.5);
        } else if (ev.by === 'shield') {
          // Knocked out on the bubble: no points, a puff and stars.
          this.spawnClip(clips.flamed, x, y, SPRITE_SCALE.creature, flip < 0 ? -1 : 1);
          this.particles.emit({ x, y, texture: this.v(V.starWhite), count: 5, speed: [60, 180], life: [300, 600], scale: [0.2, 0.35], rotationSpeed: 6 });
        } else {
          // Bonus eat: pop + the doubling number big and centred.
          this.particles.emit({ x, y, texture: this.v(V.starPink), count: 8, speed: [80, 240], life: [300, 600], scale: [0.22, 0.42], rotationSpeed: 6 });
          this.sparkle(x, y, 4);
          this.bigNumber(`${ev.points}`, state, PALETTE.bonus);
        }
        break;
      }

      // --- bonus
      case 'bonusAppear':
        this.particles.emit({ x: this.cx(ev.x), y: this.cx(ev.y), texture: this.v(V.starPink), count: 10, speed: [60, 200], life: [400, 800], scale: [0.22, 0.42], rotationSpeed: 6 });
        this.sparkle(this.cx(ev.x), this.cx(ev.y), 6);
        break;
      case 'bonusStart':
        this.floatText('+1000', px, py - C * 0.4, PALETTE.bonus, 0.7);
        this.particles.emit({ x: px, y: py, texture: this.v(V.starPink), count: 12, speed: [100, 320], life: [500, 900], scale: [0.22, 0.45], rotationSpeed: 6 });
        this.particles.emit({ x: px, y: py, texture: this.v(V.heart), count: 5, speed: [60, 180], angle: -Math.PI / 2, spread: Math.PI * 0.6, gravity: -80, life: [600, 1000], scale: [0.2, 0.32] });
        this.sparkle(px, py, 8);
        break;
      case 'bonusEnd':
        this.bonusEndAlpha = 0.35;
        this.tweens.add(350, (t) => { this.bonusEndAlpha = 0.35 * (1 - t); }, { ease: Easing.linear, tag: this.flash });
        break;

      // --- lives and phases
      case 'playerDeath': {
        const fx = this.playerFx;
        this.tweens.cancel(fx);
        this.camera.hitStop(60);
        this.camera.shake(8, 200);
        fx.sx = 1; fx.sy = 1; fx.dy = 0; fx.rot = 0;
        if (ev.cause === 'crushed') {
          this.setBust('wince', 900);
          this.pipClip.play(PIP.crushed, { hold: true });
          // The crush beat lands on Pip: the sack stops on his cell with the land squash and only
          // resumes (blending over CRUSH_BLEND_MS) if the sim keeps it falling.
          const sack = state.sacks.find((k) => k.state === 'fall' && k.x === p.x && (k.y === p.y || k.y + 1 === p.y));
          if (sack) {
            this.crushHold = { id: sack.id, x: p.x, y: p.y, t0: performance.now() };
            const sq = { sx: 1.35, sy: 0.6 };
            this.sackSquash.set(sack.id, sq);
            this.tweens.add(380, (t) => { sq.sx = 1.35 + (1 - 1.35) * t; sq.sy = 0.6 + (1 - 0.6) * t; }, { ease: Easing.outElastic, onComplete: () => this.sackSquash.delete(sack.id) });
          }
          // Dust squirts out sideways so the flattened pose stays readable.
          this.puff(px - C * 0.3, py + C * 0.35, 2, 'dust', Math.PI, 0.5, [60, 140]);
          this.puff(px + C * 0.3, py + C * 0.35, 2, 'dust', 0, 0.5, [60, 140]);
        } else {
          this.setBust('worried', 900);
          this.pipClip.play(PIP.ko, { hold: true });
          this.tweens.add(600, (t) => {
            fx.dy = -C * 0.9 * Math.sin(Math.PI * Math.min(1, t * 1.15)) + (t > 0.85 ? (C * 0.2 * (t - 0.85)) / 0.15 : 0);
            fx.rot = Math.PI * 2 * t;
          }, { ease: Easing.linear, tag: fx });
        }
        this.particles.emit({ x: px, y: py - C * 0.3, texture: this.v(V.starWhite), count: 6, speed: [60, 160], angle: -Math.PI / 2, spread: Math.PI * 0.8, gravity: 400, life: [500, 900], scale: [0.2, 0.36], rotationSpeed: 5 });
        this.dimAlpha = 0.5;
        this.tweens.cancel(this.dimG);
        this.tweens.add(400, () => { this.dimAlpha = 0.5; }, { ease: Easing.linear, tag: this.dimG, onComplete: () => {
          this.tweens.add(250, (t) => { this.dimAlpha = 0.5 * (1 - t); }, { tag: this.dimG });
        } });
        break;
      }
      case 'respawn': {
        const fx = this.playerFx;
        this.tweens.cancel(fx);
        this.tweens.cancel(this.dimG);
        this.pipClip.stop();
        this.crushHold = null;
        this.dimAlpha = 0;
        fx.dy = 0; fx.rot = 0; fx.sx = 0.2; fx.sy = 0.2;
        this.tweens.add(350, (t) => { fx.sx = 0.2 + 0.8 * t; fx.sy = 0.2 + 0.8 * t; }, { ease: Easing.outBack, tag: fx });
        this.livesPop = 1.6;
        this.tweens.add(400, (t) => { this.livesPop = 1.6 - 0.6 * t; }, { ease: Easing.outElastic, tag: this.helmets[0] });
        this.particles.clear();
        break;
      }
      case 'extraLife':
        this.floatText('1UP ♥', px, py - C * 0.6, PALETTE.bonus, 0.6);
        this.particles.emit({ x: px, y: py - C * 0.3, texture: this.v(V.heart), count: 6, speed: [60, 160], angle: -Math.PI / 2, spread: Math.PI * 0.6, gravity: -60, life: [600, 1000], scale: [0.2, 0.32] });
        this.setBust('delighted', 1200);
        this.livesPop = 1.8;
        this.tweens.add(500, (t) => { this.livesPop = 1.8 - 0.8 * t; }, { ease: Easing.outElastic, tag: this.helmets[0] });
        break;
      case 'levelStart':
        this.discovered.clear();
        this.gemReveal.clear();
        this.particles.clear();
        this.floats.removeChildren().forEach((c) => c.destroy());
        this.transients.removeChildren().forEach((c) => c.destroy());
        this.digFx.removeChildren().forEach((c) => c.destroy());
        this.creatureFx.clear();
        this.pipClip.stop();
        this.crushHold = null;
        this.dimAlpha = 0;
        this.bigNumber(`LEVEL ${ev.level}`, state, PALETTE.hudText, 0.8);
        break;
      case 'levelClear':
        for (const c of V.confetti) this.particles.emit({ x: (state.width * C) / 2, y: -C * 0.5, texture: this.v(c), count: 16, speed: [60, 220], angle: Math.PI / 2, spread: 1.2, gravity: 350, life: [900, 1500], scale: [0.16, 0.28], rotationSpeed: 9 });
        this.particles.emit({ x: (state.width * C) / 2, y: -C * 0.5, texture: this.v(V.starWhite), count: 8, speed: [60, 200], angle: Math.PI / 2, spread: 1.2, gravity: 300, life: [900, 1400], scale: [0.2, 0.35], rotationSpeed: 5 });
        this.setBust('delighted', 2500);
        break;
      case 'gameOver':
      case 'won':
      case 'timeUp':
        this.tweens.cancel(this.dimG);
        this.dimAlpha = 0.35;
        break;

      // --- objectives (WP5)
      case 'goalProgress': {
        const i = this.goalRow(state, ev.id);
        if (i >= 0) {
          this.rowPop[i] = 1.25;
          this.tweens.add(220, (t) => { this.rowPop[i] = 1.25 - 0.25 * t; }, { ease: Easing.outBack, tag: this.panelRows[i] });
        }
        break;
      }
      case 'goalComplete': {
        const i = this.goalRow(state, ev.id);
        if (i >= 0) {
          this.rowPop[i] = 1.4;
          this.tweens.add(400, (t) => { this.rowPop[i] = 1.4 - 0.4 * t; }, { ease: Easing.outElastic, tag: this.panelRows[i] });
        }
        this.setBust('delighted', 900);
        break;
      }
      case 'phaseChanged': {
        this.panelPulse = 1.3;
        this.tweens.cancel(this.panel);
        this.tweens.add(600, (t) => { this.panelPulse = 1.3 - 0.3 * t; }, { ease: Easing.outElastic, tag: this.panel });
        const name = state.objectives.phases[ev.phase]?.name;
        this.bigNumber(name ? name.toUpperCase() : `PHASE ${ev.phase + 1}`, state, PALETTE.hudText, 0.7);
        this.setBust('browsUp', 900);
        break;
      }
      case 'obstacleRaised': {
        const x = this.cx(ev.x);
        const y = this.cx(ev.y);
        if (ev.kind === 'pillar') {
          const o = this.obstacleSprite(ev.id);
          o.scale.y = 0.05;
          this.tweens.cancel(o);
          this.tweens.add(420, (t) => { o.scale.y = 0.05 + 0.95 * t; }, { ease: Easing.outBack, tag: o });
          this.puff(x, y + C * 0.4, 6, 'dirt', -Math.PI / 2, Math.PI * 0.9, [60, 180]);
          this.camera.shake(5, 180);
        } else {
          this.sparkle(x, y, 6);
        }
        break;
      }
      case 'obstacleHit': {
        const x = this.cx(ev.x);
        const y = this.cx(ev.y);
        const o = this.obstacleSprites.get(ev.id);
        if (o) {
          this.tweens.cancel(o);
          this.tweens.add(220, (t) => { o.x = x + Math.sin(t * Math.PI * 6) * C * 0.06 * (1 - t); }, { ease: Easing.linear, tag: o });
        }
        this.particles.emit({ x, y, texture: this.v(V.dirtSmall), count: 5, speed: [60, 180], gravity: 500, life: [250, 450], scale: [0.14, 0.24], rotationSpeed: 8 });
        this.sparkle(x, y, 3, { speed: [30, 90] });
        this.camera.shake(3, 100);
        break;
      }
      case 'obstacleBroken': {
        const x = this.cx(ev.x);
        const y = this.cx(ev.y);
        this.puff(x, y, ev.kind === 'pillar' ? 8 : 5, 'dirt', -Math.PI / 2, Math.PI, [80, 220]);
        if (ev.kind === 'pillar') {
          this.particles.emit({ x, y, texture: this.v(V.dirtBig), count: 6, speed: [80, 240], angle: -Math.PI / 2, spread: Math.PI * 0.8, gravity: 700, life: [400, 700], scale: [0.2, 0.34], tint: 0xbdbdc8, rotationSpeed: 6 });
          this.sparkle(x, y, 6);
          this.camera.shake(8, 200);
          this.camera.hitStop(40);
          this.setBust('gobsmacked', 900);
        }
        break;
      }
      case 'springSpawn':
        this.sparkle(this.cx(ev.x), this.cx(ev.y), 6, { speed: [40, 140], gravity: 150 });
        break;
      case 'nestOpened':
        this.puff(this.cx(ev.x), this.cx(ev.y), 8, 'dirt', -Math.PI / 2, Math.PI, [80, 220]);
        this.camera.shake(4, 160);
        this.setBust('worried', 1200);
        break;
      case 'pickupCollected':
        this.sparkle(this.cx(ev.x), this.cx(ev.y), 8);
        this.floatText(ev.kind.toUpperCase(), this.cx(ev.x), this.cx(ev.y) - C * 0.3, PALETTE.bonusGlow, 0.36);
        break;
      case 'lockUnlocked':
        this.particles.emit({ x: this.cx(ev.x), y: this.cx(ev.y), texture: this.v(V.shard), count: 8, speed: [80, 220], gravity: 600, life: [400, 700], scale: [0.16, 0.3], rotationSpeed: 8 });
        this.sparkle(this.cx(ev.x), this.cx(ev.y), 6);
        break;

      // --- kit and lantern (WP6)
      case 'lanternFill':
        this.meterPop = ev.full ? 1.3 : 1.12;
        this.tweens.cancel(this.lanternS);
        this.tweens.add(ev.full ? 400 : 200, (t) => { this.meterPop = 1 + (ev.full ? 0.3 : 0.12) * (1 - t); }, { ease: Easing.outBack, tag: this.lanternS });
        break;
      case 'goalImpossible':
        // B1 backstop: the Target cannot be met any more; the level restarts. Grandpa winces, the panel shakes.
        this.floatText('TARGET MISSED', this.cx(state.width / 2 - 0.5), this.cx(state.height * 0.4), 0xffd0a0, 0.7);
        this.setBust('wince', 1200);
        this.fireShake = 1;
        break;
      case 'chargeBanked':
        this.chargePop = 1.5;
        this.tweens.cancel(this.charges[0]);
        this.tweens.add(350, (t) => { this.chargePop = 1.5 - 0.5 * t; }, { ease: Easing.outElastic, tag: this.charges[0] });
        if (ev.charges >= DEFAULTS.lanternChargesPerBeam) this.setBust('browsUp', 800);
        break;
      case 'beam': {
        this.beamGlow = 1;
        this.tweens.cancel(this.beamParts[0]);
        this.tweens.add(DEFAULTS.beamTicks * DEFAULTS.tickMs + 200, (t) => { this.beamGlow = 1 - t; }, { ease: Easing.outQuad, tag: this.beamParts[0] });
        this.camera.hitStop(50);
        this.camera.shake(10, 260);
        for (let i = 1; i <= ev.length; i++) {
          const x = this.cx(ev.x + DX[ev.dir] * i);
          const y = this.cx(ev.y + DY[ev.dir] * i);
          if (i % 2 === 0) this.sparkle(x, y, 2, { speed: [20, 90] });
        }
        this.setBust(ev.kills > 0 ? 'gobsmacked' : 'delighted', 1000);
        break;
      }
      case 'beamBlocked':
        this.fireShake = 1;
        this.tweens.cancel(this.lanternS);
        this.tweens.add(220, (t) => { this.fireShake = 1 - t; }, { ease: Easing.linear, tag: this.lanternS });
        break;
      case 'kitApplied': {
        if (ev.kind === 'shield') {
          this.bubble.scale.set(0.2);
          this.tweens.cancel(this.bubble);
          this.tweens.add(380, (t) => { this.bubble.scale.set((0.2 + 0.8 * t) * SPRITE_SCALE.pip * 1.5); }, { ease: Easing.outBack, tag: this.bubble });
          this.sparkle(px, py, 6);
        } else if (ev.kind === 'turbo') {
          this.spawnClip({ duration: 360, keys: [{ frame: ASSETS.turbo.burst, at: 0, sx: 0.8, sy: 0.8 }, { frame: ASSETS.turbo.burst, at: 0.4, sx: 1.5, sy: 1.5 }, { frame: ASSETS.turbo.burst, at: 1, sx: 1.9, sy: 1.9, alpha: 0 }] }, px, py, 1);
          this.camera.shake(4, 150);
        } else if (ev.kind === 'beam') {
          this.chargePop = 1.6;
          this.tweens.add(500, (t) => { this.chargePop = 1.6 - 0.6 * t; }, { ease: Easing.outElastic, tag: this.charges[0] });
          this.sparkle(px, py, 8);
        }
        this.floatText(ev.kind.toUpperCase(), px, py - C * 0.5, PALETTE.bonusGlow, 0.36);
        break;
      }
      case 'shieldPop':
        if (ev.cause !== 'timeout') {
          this.spawnClip({ duration: 320, keys: [{ frame: ASSETS.shield[2], at: 0, sx: 1.4, sy: 1.4 }, { frame: ASSETS.shield[2], at: 1, sx: 2, sy: 2, alpha: 0 }] }, px, py, SPRITE_SCALE.pip);
          this.particles.emit({ x: px, y: py, texture: this.v(V.shard), count: 8, speed: [80, 220], gravity: 300, life: [350, 600], scale: [0.14, 0.26], rotationSpeed: 8 });
        }
        this.setBust(ev.cause === 'timeout' ? 'neutral' : 'wince', 600);
        break;
      case 'turboEnd':
        this.puff(px, py + C * 0.3, 4, 'dust', -Math.PI / 2, Math.PI, [30, 100]);
        break;
      default:
        break;
    }
  }

  /** Row index of a goal in the Target panel (goals of the current phase, first three), or -1. */
  private goalRow(state: SimState, id: string): number {
    return this.currentGoals(state).findIndex((g) => g.id === id);
  }

  private currentGoals(state: SimState): Goal[] {
    return state.objectives.goals.filter((g) => g.phase === state.objectives.phase).slice(0, 3);
  }

  private obstacleSprite(id: string): Sprite {
    let sp = this.obstacleSprites.get(id);
    if (!sp) {
      sp = new Sprite(this.atlas.get(ASSETS.obstacle.rubble));
      sp.anchor.set(0.5);
      this.objects.addChild(sp);
      this.obstacleSprites.set(id, sp);
    }
    return sp;
  }

  private setBust(state: BustState, ms: number): void {
    this.bustOverride = { state, until: performance.now() + ms };
  }

  /** Temporary sprite in the fx layer driven by a tween callback (p = 0..1), destroyed at the end. */
  private spawnTransient(texture: Texture, x: number, y: number, ms: number, fn: (p: number, sp: Sprite) => void, layer: Container = this.transients): void {
    const sp = new Sprite(texture);
    sp.anchor.set(0.5);
    sp.position.set(x, y);
    layer.addChild(sp);
    fn(0, sp);
    this.tweens.add(ms, (p) => fn(p, sp), { ease: Easing.linear, onComplete: () => { layer.removeChild(sp); sp.destroy(); } });
  }

  /** Floating score text: pops in, rises ~0.9 cells and fades. */
  floatText(text: string, x: number, y: number, color: number, sizeCells = 0.5): void {
    const t = makeText(text, Math.round(C * sizeCells), { bold: true, color });
    t.anchor.set(0.5);
    t.position.set(x, y);
    this.floats.addChild(t);
    this.tweens.add(950, (p) => {
      t.y = y - C * 0.9 * p;
      t.scale.set(p < 0.15 ? 0.6 + 0.4 * (p / 0.15) * 1.3 : 1.12 - 0.12 * ((p - 0.15) / 0.85));
      t.alpha = p > 0.6 ? 1 - (p - 0.6) / 0.4 : 1;
    }, { ease: Easing.outQuad, onComplete: () => { this.floats.removeChild(t); t.destroy(); } });
  }

  /** Big centred number (bonus doubling, level title): punches in, holds, fades. */
  private bigNumber(text: string, state: SimState, color: number, sizeCells = 1.3): void {
    // Only one big number at a time: a fast chain of eats replaces rather than stacks.
    if (this.lastBig) {
      this.tweens.cancel(this.lastBig);
      this.floats.removeChild(this.lastBig);
      this.lastBig.destroy();
      this.lastBig = null;
    }
    const t = makeText(text, Math.round(C * sizeCells), { bold: true, color, align: 'center' });
    this.lastBig = t;
    t.anchor.set(0.5);
    const x = (state.width * C) / 2;
    const y = state.height * C * 0.42;
    t.position.set(x, y);
    this.floats.addChild(t);
    this.tweens.add(900, (p) => {
      const s = p < 0.2 ? 0.3 + (1.35 - 0.3) * Easing.outBack(p / 0.2) : 1.35 - 0.2 * ((p - 0.2) / 0.8);
      t.scale.set(s);
      t.y = y - C * 0.3 * p;
      t.alpha = p > 0.65 ? 1 - (p - 0.65) / 0.35 : 1;
    }, { ease: Easing.linear, tag: t, onComplete: () => { this.floats.removeChild(t); t.destroy(); if (this.lastBig === t) this.lastBig = null; } });
  }

  // -------------------------------------------------------------------------

  draw(state: SimState, alpha: number, L: Layout, settings: Settings, hud: HudInfo): void {
    this.closedSack = !!hud.closedSack;
    this.camera.x = L.fieldX;
    this.camera.y = L.fieldY;
    this.camera.scale = L.cell / C;
    this.camera.apply(this.world);
    this.syncCells(state);
    this.syncGems(state, settings);
    this.syncObstacles(state);
    this.syncGold(state);
    this.syncSacks(state, alpha);
    this.syncBonus(state, alpha);
    this.syncFlame(state, alpha);
    this.syncCreatures(state, alpha);
    this.syncPlayer(state, alpha);
    this.syncFlameIcon(state, alpha, hud);
    this.syncShadows(state, alpha);
    this.syncOverlays(state, alpha, hud);
    this.syncHud(state, L, settings, hud);
    this.app.renderer.render(this.app.stage);
  }

  private syncCells(state: SimState): void {
    const key = `${state.width}x${state.height}`;
    if (key !== this.cellsKey) {
      this.earth.removeChildren().forEach((c) => c.destroy());
      this.cells = [];
      const mat = this.atlas.get(ASSETS.earthMaterial);
      this.earthTiling = new TilingSprite({ texture: mat, width: state.width * C, height: state.height * C });
      // The material sheet spans 4×4 cells; tile it so its own speckle carries across cells.
      this.earthTiling.tileScale.set((C * 4) / mat.width, (C * 4) / mat.height);
      this.earth.addChild(this.earthTiling);
      for (let y = 0; y < state.height; y++) {
        for (let x = 0; x < state.width; x++) {
          const s = new Sprite(this.atlas.get(ASSETS.tunnel.fill));
          s.position.set(x * C, y * C);
          s.width = C;
          s.height = C;
          s.visible = false;
          this.earth.addChild(s);
          this.cells.push(s);
        }
      }
      this.cellsKey = key;
      this.cellTint = 0xffffff;
      this.rimKey = '';
    }
    const tint = state.bonusActive ? BONUS_CELL_TINT : 0xffffff;
    const retint = tint !== this.cellTint;
    this.cellTint = tint;
    if (retint && this.earthTiling) this.earthTiling.tint = tint;
    const fillTex = this.atlas.get(ASSETS.tunnel.fill);
    for (let i = 0; i < this.cells.length; i++) {
      const c = this.cells[i];
      c.visible = state.dug[i] === 1;
      if (c.texture !== fillTex) {
        c.texture = fillTex;
        c.width = C;
        c.height = C;
      }
      if (retint) c.tint = tint;
    }
    this.syncRims(state);
  }

  /**
   * Scalloped (scooped) tunnel edges: where a dug cell borders dirt, the dirt bulges into the
   * tunnel as a row of rounded bumps with a darker underside. Rebuilt only when the dug map changes.
   */
  private syncRims(state: SimState): void {
    const key = state.dug.join('');
    if (key === this.rimKey) return;
    this.rimKey = key;
    const g = this.rimG;
    g.clear();
    const W = state.width;
    const H = state.height;
    const dugAt = (x: number, y: number): boolean => x >= 0 && y >= 0 && x < W && y < H && state.dug[y * W + x] === 1;
    const r = C * 0.2;
    const step = C / 3;
    const mat = this.atlas.get(ASSETS.earthMaterial);
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        if (!dugAt(x, y)) continue;
        const px = x * C;
        const py = y * C;
        // Cool shadow along the dirt side first, bumps on top.
        const edges: [boolean, number, number, number, number][] = [
          [!dugAt(x, y - 1), px, py, 1, 0], // top edge: bumps hang down
          [!dugAt(x, y + 1), px, py + C, 1, 0], // bottom edge: bumps rise up
          [!dugAt(x - 1, y), px, py, 0, 1], // left edge
          [!dugAt(x + 1, y), px + C, py, 0, 1], // right edge
        ];
        for (const [on, ex, ey, dx, dy] of edges) {
          if (!on) continue;
          for (let k = 0; k < 3; k++) {
            const cx = ex + dx * (step * (k + 0.5));
            const cy = ey + dy * (step * (k + 0.5));
            // Direction into the tunnel
            const inX = dx === 0 ? (ex === px ? 1 : -1) : 0;
            const inY = dy === 0 ? (ey === py ? 1 : -1) : 0;
            // Cool shadow under the bump, the bump itself in the real earth material, a warm top-left highlight.
            g.circle(cx + inX * r * 0.6, cy + inY * r * 0.6, r * 1.08).fill({ color: PALETTE.earthDark, alpha: 0.6 });
            g.circle(cx + inX * r * 0.35, cy + inY * r * 0.35, r).fill({ texture: mat, textureSpace: 'local' });
            g.circle(cx + inX * r * 0.35, cy + inY * r * 0.35, r).stroke({ color: PALETTE.earthDark, width: 1.5, alpha: 0.35 });
            g.circle(cx + inX * r * 0.25 - r * 0.3, cy + inY * r * 0.25 - r * 0.3, r * 0.4).fill({ color: 0xffffff, alpha: 0.16 });
          }
        }
      }
    }
  }

  /**
   * A3: which gems Pip has "seen". A gem is discovered when any 4-neighbour is dug (an adjacent
   * dig) or when it sits in the headlamp cone: up to three cells ahead in the facing direction,
   * one cell wide at depth 1 and three wide beyond. Discovery is remembered for the level. Render
   * only: the sim and its replay hash never see it.
   */
  private updateDiscovery(state: SimState): void {
    const W = state.width;
    const H = state.height;
    const reveal = (x: number, y: number): void => {
      if (x < 0 || y < 0 || x >= W || y >= H) return;
      const i = y * W + x;
      if (state.gems[i] && !this.discovered.has(i)) {
        this.discovered.add(i);
        this.gemReveal.set(i, state.tick);
        const s = this.gemSprites.get(i);
        if (s) this.sparkle(s.x, s.y, 3);
      }
    };
    for (let i = 0; i < state.gems.length; i++) {
      if (!state.gems[i] || this.discovered.has(i)) continue;
      const x = i % W;
      const y = (i - x) / W;
      if ((x > 0 && state.dug[i - 1]) || (x < W - 1 && state.dug[i + 1]) || (y > 0 && state.dug[i - W]) || (y < H - 1 && state.dug[i + W])) reveal(x, y);
    }
    const p = state.player;
    if (p.alive && p.facing !== 0) {
      const dx = DX[p.facing];
      const dy = DY[p.facing];
      for (let k = 1; k <= 3; k++) {
        const cx = p.x + dx * k;
        const cy = p.y + dy * k;
        reveal(cx, cy);
        if (k >= 2) {
          reveal(cx + dy, cy + dx);
          reveal(cx - dy, cy - dx);
        }
      }
    }
  }

  /** The upper part of a gem texture: what shows above the dirt while a gem is undiscovered. */
  private gemTop(name: string): Texture {
    const full = this.atlas.get(name);
    const key = `${name}#${full.source.uid}`;
    let t = this.gemTops.get(key);
    if (!t) {
      const f = full.frame;
      t = new Texture({ source: full.source, frame: new Rectangle(f.x, f.y, f.width, f.height * 0.55) });
      this.gemTops.set(key, t);
    }
    return t;
  }

  private syncGems(state: SimState, settings: Settings): void {
    const seen = new Set<number>();
    const discovery = settings.gemDiscovery;
    if (discovery) this.updateDiscovery(state);
    const bg = this.buriedG;
    bg.clear();
    for (let i = 0; i < state.gems.length; i++) {
      if (!state.gems[i]) continue;
      seen.add(i);
      let s = this.gemSprites.get(i);
      if (!s) {
        const x = i % state.width;
        const y = Math.floor(i / state.width);
        s = new Sprite(this.atlas.get(`gem.${state.gemTypes[i] % PALETTE.gems.length}`));
        void x;
        void y;
        s.anchor.set(0.5);
        s.position.set(this.cx(x), this.cx(y));
        s.zIndex = 1;
        this.objects.addChild(s);
        this.gemSprites.set(i, s);
      }
      const gname = `gem.${state.gemTypes[i] % PALETTE.gems.length}`;
      const buried = discovery && !this.discovered.has(i);
      if (buried) {
        const top = this.gemTop(gname);
        if (s.texture !== top) s.texture = top;
      } else this.tex(s, gname);
      // Idle sparkle: a brief brighten every few seconds, staggered per gem (a buried gem only glints).
      const period = buried ? 36 : 50;
      const ph = ((state.tick + i * 7) % period) / period;
      let sc = SPRITE_SCALE.gem * (ph < 0.08 ? 1 + 0.25 * Math.sin((ph / 0.08) * Math.PI) : 1);
      const cy = this.cx(Math.floor(i / state.width));
      if (buried) {
        // Only the top of the gem shows above the dirt, dimmer, sitting a touch low in its cell;
        // a thin shadow line where the dirt meets it. The glint (idle sparkle) still plays.
        s.anchor.set(0.5, 1);
        s.alpha = 0.8;
        s.y = cy + C * 0.06;
        const hw = s.width * 0.5;
        bg.rect(s.x - hw, cy + C * 0.05, hw * 2, C * 0.04).fill({ color: PALETTE.earthDark, alpha: 0.45 });
      } else {
        s.anchor.set(0.5, 0.5);
        s.alpha = 1;
        s.y = cy;
        const rt = this.gemReveal.get(i);
        if (rt !== undefined) {
          const t = (state.tick - rt) / 6;
          if (t < 1) sc *= 1 + 0.35 * Math.sin(t * Math.PI);
          else this.gemReveal.delete(i);
        }
      }
      s.scale.set(sc);
      if ((state.tick + i * 7) % period === 0 && state.tick !== this.gemSparkleTick.get(i)) {
        this.gemSparkleTick.set(i, state.tick);
        this.particles.emit({ x: s.x + C * 0.18, y: s.y - C * 0.18, texture: this.v(V.sparkleSmall), count: 1, speed: [0, 10], life: [350, 450], scale: [0.16, 0.24], rotationSpeed: 2 });
      }
    }
    for (const [i, s] of this.gemSprites) {
      if (!seen.has(i)) {
        this.objects.removeChild(s);
        s.destroy();
        this.gemSprites.delete(i);
      }
    }
  }

  /**
   * Obstacles (WP5): one sprite per obstacle keyed by id, texture by state. Pillars rise on their
   * event (scale tween) and leave rubble; hard dirt overlays the earth until cleared; locked gems
   * keep their case until unlocked; springs glow while they can fire; nests open at their phase.
   */
  private syncObstacles(state: SimState): void {
    const seen = new Set<string>();
    const O = ASSETS.obstacle;
    const now = performance.now();
    for (const o of state.objectives.obstacles) {
      const key = o.id;
      const sp = this.obstacleSprite(key);
      seen.add(key);
      sp.position.set(this.cx(o.x), this.cx(o.y));
      sp.zIndex = 0;
      sp.alpha = 1;
      let visible = true;
      let scale = 1;
      switch (o.type) {
        case 'pillar':
          if (o.done) {
            this.tex(sp, O.rubble);
            scale = 0.9;
          } else if (o.active) {
            const frac = o.hits > 0 ? o.hitsLeft / o.hits : 1;
            this.tex(sp, O.pillar[frac > 0.67 ? 0 : frac > 0.34 ? 1 : 2]);
            sp.zIndex = 6;
          } else visible = false;
          break;
        case 'hardDirt':
          if (o.done) visible = false;
          else {
            this.tex(sp, O.hardDirt[o.cracked ? 1 : 0]);
            sp.width = C;
            sp.height = C;
            sp.zIndex = 0;
          }
          break;
        case 'lockedGem':
          if (o.unlocked || o.done) visible = false;
          else {
            this.tex(sp, O.lockedGem);
            sp.zIndex = 2;
            scale = 0.9;
          }
          break;
        case 'gemSpring': {
          const standing = state.objectives.obstacles.some((p) => p.type === 'pillar' && p.active && !p.done && p.phase === o.phase);
          const live = o.active && standing;
          this.tex(sp, live ? O.springActive : O.springDormant);
          scale = live ? 0.95 + 0.05 * Math.sin(now / 160) : 0.9;
          break;
        }
        case 'nest':
          this.tex(sp, o.active ? O.nestOpen : O.nestClosed);
          scale = 0.95;
          break;
        default:
          visible = false;
          break;
      }
      sp.visible = visible;
      if (visible && o.type !== 'hardDirt') {
        const raising = this.tweens.active > 0 && o.type === 'pillar' && sp.scale.y < 0.99 && sp.scale.y !== scale;
        sp.scale.set(scale, raising ? sp.scale.y : scale);
      }
      // Switch tile for a locked gem.
      if (o.type === 'lockedGem' && o.switchCell) {
        const sk = `${key}.switch`;
        const sw = this.obstacleSprite(sk);
        seen.add(sk);
        this.tex(sw, O.switch);
        sw.position.set(this.cx(o.switchCell.x), this.cx(o.switchCell.y));
        sw.zIndex = 0;
        sw.visible = !o.unlocked;
        sw.scale.set(0.8);
      }
    }
    for (const [k, sp] of this.obstacleSprites) {
      if (!seen.has(k)) {
        this.objects.removeChild(sp);
        sp.destroy();
        this.obstacleSprites.delete(k);
      }
    }
    // Pickups (inert in WP5): a glowing round marker on the cell.
    const seenP = new Set<string>();
    state.objectives.pickups.forEach((p, i) => {
      const k = `${i}`;
      let sp = this.pickupSprites.get(k);
      if (!sp) {
        sp = new Sprite(this.atlas.get(ASSETS.ui.iconRound));
        sp.anchor.set(0.5);
        sp.zIndex = 3;
        this.objects.addChild(sp);
        this.pickupSprites.set(k, sp);
      }
      seenP.add(k);
      sp.visible = p.active && !p.collected;
      const icon = p.type === 'fruit' ? ASSETS.kitFruit : p.type === 'shield' ? ASSETS.kit.shield : p.type === 'turbo' ? ASSETS.kit.turbo : p.type === 'beam' ? ASSETS.kit.beam : ASSETS.ui.iconRound;
      this.tex(sp, icon);
      sp.position.set(this.cx(p.x), this.cx(p.y) - C * 0.04 * Math.sin(now / 300 + i));
      sp.scale.set(0.42 + 0.03 * Math.sin(now / 200 + i));
    });
    for (const [k, sp] of this.pickupSprites) {
      if (!seenP.has(k)) {
        this.objects.removeChild(sp);
        sp.destroy();
        this.pickupSprites.delete(k);
      }
    }
  }

  private syncGold(state: SimState): void {
    const seen = new Set<string>();
    for (const g of state.golds) {
      const k = `${g.x},${g.y}`;
      seen.add(k);
      let s = this.goldSprites.get(k);
      if (!s) {
        s = new Sprite(this.atlas.get('gold'));
        s.anchor.set(0.5);
        s.position.set(this.cx(g.x), this.cx(g.y));
        s.zIndex = 2;
        this.objects.addChild(s);
        this.goldSprites.set(k, s);
      }
      this.tex(s, 'gold');
      s.scale.set(SPRITE_SCALE.gold);
      s.alpha = g.timer < 20 && Math.floor(g.timer / 2) % 2 === 0 ? 0.25 : 1;
      const last = this.goldLastTimer.get(k);
      const every = g.timer < 30 ? 3 : 8;
      if (last !== g.timer && g.timer % every === 0) {
        this.particles.emit({ x: this.cx(g.x) + (Math.random() - 0.5) * C * 0.5, y: this.cx(g.y) + (Math.random() - 0.3) * C * 0.3, texture: this.v(V.sparkleSmall), count: 1, speed: [10, 40], angle: -Math.PI / 2, spread: 0.5, life: [300, 500], scale: [0.15, 0.3] });
      }
      this.goldLastTimer.set(k, g.timer);
    }
    for (const [k, s] of this.goldSprites) {
      if (!seen.has(k)) {
        this.objects.removeChild(s);
        s.destroy();
        this.goldSprites.delete(k);
        this.goldLastTimer.delete(k);
      }
    }
  }

  private syncSacks(state: SimState, alpha: number): void {
    const seen = new Set<number>();
    for (const sk of state.sacks) {
      seen.add(sk.id);
      let s = this.sackSprites.get(sk.id);
      if (!s) {
        s = new Sprite(this.atlas.get('sack'));
        s.anchor.set(0.5, 0.97);
        s.zIndex = 4;
        this.objects.addChild(s);
        this.sackSprites.set(sk.id, s);
      }
      let fx = sk.x;
      let fy = sk.y;
      if (sk.slideT > 0) {
        const t = Math.min(1, Math.max(0, 1 - (sk.slideT - alpha) / state.playerTicksH));
        fx = sk.slideFromX + (sk.x - sk.slideFromX) * t;
      }
      if (sk.state === 'fall') fy = sk.y + Math.min(1, (sk.fallProg + alpha) / DEFAULTS.sackFallTicksPerCell);
      const hold = this.crushHold;
      if (hold && hold.id === sk.id) {
        const age = performance.now() - hold.t0;
        if (age < CRUSH_HOLD_MS) fy = hold.y;
        else if (age < CRUSH_HOLD_MS + CRUSH_BLEND_MS) fy = hold.y + (fy - hold.y) * Easing.outQuad((age - CRUSH_HOLD_MS) / CRUSH_BLEND_MS);
        else this.crushHold = null;
      }
      s.position.set(this.cx(fx), this.cx(fy) + C * 0.47);
      const sq0 = this.sackSquash.get(sk.id);
      const landing = (!!sq0 && sq0.sy < 0.92) || (!!this.crushHold && this.crushHold.id === sk.id);
      const closed = !!this.closedSack;
      // Keyframe clips: wobble rocks through the lean frames (phase over 4 ticks, amplitude ramping
      // up over the wobble), fall stretches and sways; the closed sack (dev A/B) keeps its own frame.
      const boulder = sk.kind === 'boulder';
      let name: string = boulder ? ASSETS.obstacle.boulder : closed ? ASSETS.sackState.closed : ASSETS.sackState.rest;
      let sx = 1;
      let sy = 1;
      let rot = 0;
      if (sk.state === 'wobble') {
        const ramp = 0.25 + (0.75 * (sk.wobble + alpha)) / DEFAULTS.sackWobbleTicks;
        const pose = samplePhase(SACK.wobble, (sk.wobble + alpha) / 4);
        if (!closed && !boulder) name = pose.frame;
        rot = pose.rot * ramp;
        sx = 1 + (pose.sx - 1) * ramp;
      } else if (sk.state === 'fall' && !landing) {
        const pose = samplePhase(SACK.fall, (sk.fallProg + alpha) / DEFAULTS.sackFallTicksPerCell);
        if (!closed && !boulder) name = pose.frame;
        sx = pose.sx;
        sy = pose.sy;
        rot = pose.rot;
      } else if (landing && !closed && !boulder) {
        name = ASSETS.sackState.land;
      } else if (sk.slideT > 0 && !closed && !boulder) {
        name = sk.x > sk.slideFromX ? ASSETS.sackState.leanR : ASSETS.sackState.leanL;
      }
      this.tex(s, name);
      s.rotation = rot;
      const sq = this.sackSquash.get(sk.id);
      if (sq) {
        sx *= sq.sx;
        sy *= sq.sy;
      }
      s.scale.set(sx * SPRITE_SCALE.sack, sy * SPRITE_SCALE.sack);
    }
    for (const [id, s] of this.sackSprites) {
      if (!seen.has(id)) {
        this.objects.removeChild(s);
        s.destroy();
        this.sackSprites.delete(id);
        this.sackSquash.delete(id);
      }
    }
  }

  private syncBonus(state: SimState, alpha: number): void {
    const b = state.bonusItem;
    this.bonus.visible = !!b;
    this.bonusHalo.visible = !!b;
    if (!b) return;
    this.tex(this.bonus, 'bonus');
    this.bonus.position.set(this.cx(b.x), this.cx(b.y));
    this.bonusHalo.position.set(this.cx(b.x), this.cx(b.y));
    const pulse = 0.85 + 0.15 * Math.sin((state.tick + alpha) * 0.6);
    this.bonus.scale.set(pulse * SPRITE_SCALE.bonus);
    this.bonusHalo.scale.set(1.6 + 0.4 * pulse);
    this.bonusHalo.alpha = 0.5;
  }

  private syncFlame(state: SimState, alpha: number): void {
    const f = state.flame;
    this.flame.visible = !!f;
    if (!f) {
      this.flameLastCell = '';
      return;
    }
    const x = this.cx(f.x) + DX[f.dir] * C * 0.2;
    const y = this.cx(f.y) + DY[f.dir] * C * 0.2;
    this.flame.position.set(x, y);
    this.flame.rotation = DIR_ROT[f.dir];
    // ART4 flame frames cycle as it flies; the last frame is the burst (used by flameEnd's puff).
    this.tex(this.flame, ASSETS.flameFrames[Math.floor(performance.now() / 60) % 4]);
    const pulse = 1 + 0.15 * Math.sin((state.tick + alpha) * 9);
    this.flame.scale.set(0.95 * pulse, 0.85 * pulse);
    const key = `${f.x},${f.y}`;
    if (key !== this.flameLastCell) {
      this.flameLastCell = key;
      this.spawnTransient(this.atlas.get(ASSETS.flameFrames[2]), x, y, 140, (t, sp) => {
        sp.rotation = DIR_ROT[f.dir];
        sp.alpha = 0.6 * (1 - t);
        sp.scale.set(0.8 - 0.3 * t, 0.7 - 0.3 * t);
      });
    }
    this.particles.emit({ x: x + (Math.random() - 0.5) * C * 0.4, y, texture: this.v(V.dustWhite), count: 1, speed: [20, 60], angle: -Math.PI / 2, spread: 0.6, life: [120, 260], scale: [0.15, 0.3], tint: PALETTE.flameCore, alpha: 0.45 });
  }

  private syncCreatures(state: SimState, alpha: number): void {
    const seen = new Set<number>();
    for (const c of state.creatures) {
      seen.add(c.id);
      let s = this.creatureSprites.get(c.id);
      if (!s) {
        s = new Sprite(this.atlas.get('crawler'));
        s.anchor.set(0.5);
        s.zIndex = 8;
        this.objects.addChild(s);
        this.creatureSprites.set(c.id, s);
      }
      const f = this.cfx(c.id);
      const phase = c.moving ? (c.prog + alpha) / c.ticks : 0;
      // Clip selection: a playing mutation strip overrides; otherwise the state picks the cycle.
      let pose: Pose | null = f.clip.advance(this.dt);
      if (!pose) {
        if (c.kind === 'borer') {
          const tx = c.x + DX[c.dir];
          const ty = c.y + DY[c.dir];
          const intoDirt = c.moving && tx >= 0 && ty >= 0 && tx < state.width && ty < state.height && state.dug[ty * state.width + tx] !== 1;
          pose = intoDirt ? sampleTime(BORER.eat, performance.now()) : samplePhase(BORER.dig, phase);
        } else if (state.bonusActive) pose = samplePhase(CRAWLER.flee, phase);
        else if (!c.moving && c.frustration > 0) pose = sampleTime(CRAWLER.stuck, performance.now() + c.id * 137);
        else pose = samplePhase(CRAWLER.waddle, phase);
      }
      this.tex(s, pose.frame);
      this.creatureLastKind.set(c.id, c.kind);
      s.tint = state.bonusActive ? PALETTE.flee : 0xffffff;
      const [fx, fy] = moverPos(c, alpha);
      let x = this.cx(fx);
      let y = this.cx(fy);
      // Frustration is visible: jitter grows with the counter toward the mutate threshold.
      if (c.kind === 'crawler' && c.frustration > 0) {
        const j = Math.min(1, c.frustration / Math.max(1, state.params.mutateThreshold));
        x += (Math.random() - 0.5) * C * 0.14 * j;
        y += (Math.random() - 0.5) * C * 0.14 * j;
      }
      this.creatureLastPos.set(c.id, [x, y]);
      // Lean toward the direction it is about to take (turn penalty tick, plus the event-driven decay).
      const lean = Math.max(f.lean, c.turnPenalty > 0 ? 1 : 0);
      const flip = c.dir === 4 ? -1 : 1;
      let sx = f.scale * pose.sx;
      let sy = f.scale * pose.sy;
      let rot = pose.rot * flip;
      if (lean > 0) {
        if (c.dir === 2 || c.dir === 4) rot += 0.28 * lean * (c.dir === 2 ? 1 : -1);
        else if (c.dir === 1) { sy *= 1 + 0.2 * lean; sx *= 1 - 0.12 * lean; }
        else if (c.dir === 3) { sy *= 1 - 0.18 * lean; sx *= 1 + 0.14 * lean; }
      }
      s.position.set(x + pose.dx * C * flip, y + pose.dy * C);
      s.alpha = pose.alpha;
      s.rotation = rot;
      s.scale.set(flip * sx * SPRITE_SCALE.creature, sy * SPRITE_SCALE.creature);
    }
    for (const [id, s] of this.creatureSprites) {
      if (!seen.has(id)) {
        this.objects.removeChild(s);
        s.destroy();
        this.creatureSprites.delete(id);
        this.creatureFx.delete(id);
        this.creatureLastKind.delete(id);
      }
    }
  }

  private syncPlayer(state: SimState, alpha: number): void {
    const p = state.player;
    const show = p.alive || state.phase === 'dying';
    this.player.visible = show;
    this.lamp.visible = show && p.alive;
    this.arrow.visible = false;
    this.chainT.visible = false;
    if (!show) return;
    const [fx, fy] = moverPos(p, alpha);
    const px = this.cx(fx);
    const py = this.cx(fy);
    const f = this.playerFx;
    const now = performance.now();
    // One-shot event clips (dig / turn / fire / crushed / ko) override the state-driven cycle.
    let pose: Pose | null = this.pipClip.advance(this.dt);
    if (!pose) {
      if (!p.alive) pose = samplePhase(PIP.ko, 0);
      else if (state.phase === 'levelClear') pose = sampleTime(PIP.clear, now);
      else if (state.bonusActive && !p.moving) pose = sampleTime(PIP.bonus, now);
      else if (p.moving) pose = samplePhase(PIP.walk, (p.prog + alpha) / p.ticks);
      else pose = sampleTime(PIP.idle, now);
    }
    const flip = p.facing === 4 ? -1 : 1;
    // Turbo swaps in the ART4 sprint frames and leaves a dust trail.
    if (p.turbo > 0 && p.alive && p.moving) {
      pose = { ...pose, frame: ASSETS.turbo.frames[Math.floor((p.prog + alpha) * 1.5) % 3] };
      if (now - this.turboTrailAt > 60) {
        this.turboTrailAt = now;
        this.particles.emit({ x: px - DX[p.facing] * C * 0.3, y: py + C * 0.2, texture: this.v(V.dustWhite), count: 1, speed: [10, 40], angle: Math.atan2(-DY[p.facing], -DX[p.facing] || 0.001), spread: 0.4, life: [200, 350], scale: [0.2, 0.35], alpha: 0.7 });
      }
    }
    this.tex(this.player, pose.frame);
    this.player.position.set(px + pose.dx * C * flip, py + pose.dy * C + f.dy);
    this.player.rotation = pose.rot * flip + f.rot;
    this.player.scale.set(flip * pose.sx * f.sx * SPRITE_SCALE.pip, pose.sy * f.sy * SPRITE_SCALE.pip);
    const blink = p.invincible > 0 && Math.floor((state.tick + alpha) / 2) % 2 === 1;
    this.player.alpha = blink ? 0.25 : pose.alpha;
    // Shield bubble: intact, cracking in the last 3 s, popped by its event.
    this.bubble.visible = p.shield > 0 && p.alive;
    if (this.bubble.visible) {
      this.tex(this.bubble, ASSETS.shield[p.shield < 38 ? 1 : 0]);
      this.bubble.position.set(px, py + f.dy);
      if (!this.tweens.active || this.bubble.scale.x > SPRITE_SCALE.pip * 1.4) this.bubble.scale.set(SPRITE_SCALE.pip * 1.5 * (1 + 0.03 * Math.sin(now / 180)));
      this.bubble.alpha = p.shield < 38 && Math.floor(now / 120) % 2 === 0 ? 0.6 : 0.95;
    }
    this.syncBeam(state);
    // Headlamp lights the tunnel ahead.
    this.lamp.position.set(px + DX[p.facing] * C * 0.75, py + DY[p.facing] * C * 0.75);
    this.lamp.scale.set(1.7 + 0.1 * Math.sin((state.tick + alpha) * 0.9), 1.3);
    this.lamp.rotation = DIR_ROT[p.facing];
    this.lamp.alpha = 0.42;
    if (p.queued !== 0 && p.alive) {
      this.arrow.visible = true;
      this.arrow.position.set(px + DX[p.queued] * C * 0.8, py + DY[p.queued] * C * 0.8);
      this.arrow.rotation = DIR_ROT[p.queued];
      this.arrow.scale.set(1 + 0.5 * this.arrowPulse);
    }
    // Chain counter during an octave attempt.
    if (p.alive && state.chainCount >= 2 && state.chainTimer > 0) {
      this.chainT.visible = true;
      this.chainT.text = `${state.chainCount}/${DEFAULTS.octaveLength}`;
      const k = state.chainCount / DEFAULTS.octaveLength;
      this.chainT.style.fontSize = Math.round(C * (0.3 + 0.35 * k));
      this.chainT.position.set(px, py - C * 0.45);
      this.chainT.alpha = 0.6 + 0.4 * Math.min(1, state.chainTimer / DEFAULTS.chainTicks);
    }
  }

  /** Beam (WP6): head at Pip, body segments to the edge, tail at the end, fading over beamTicks. */
  private syncBeam(state: SimState): void {
    const b = state.beam;
    const show = !!b && this.beamGlow > 0.01;
    for (const sp of this.beamParts) sp.visible = false;
    if (!b || !show) return;
    const rot = DIR_ROT[b.dir];
    const a = 0.35 + 0.65 * this.beamGlow;
    let k = 0;
    const place = (name: string, i: number, w: number): void => {
      const sp = this.beamParts[k++];
      if (!sp) return;
      this.tex(sp, name);
      sp.visible = true;
      sp.position.set(this.cx(b.x + DX[b.dir] * i), this.cx(b.y + DY[b.dir] * i));
      sp.rotation = rot;
      sp.scale.set(w, 0.9 + 0.2 * this.beamGlow);
      sp.alpha = a;
    };
    place(ASSETS.beam.head, 0.5, 1);
    for (let i = 2; i <= b.length - 1; i += 3) place(ASSETS.beam.body, i, 1);
    place(ASSETS.beam.tail, b.length - 0.5, 1);
  }

  /** Variant B: the lantern meter sits beside Pip (mini), with its charge lanterns. */
  private syncFlameIcon(state: SimState, alpha: number, hud: HudInfo): void {
    const p = state.player;
    const show = hud.fireVariant === 'doubleTap' && hud.mode !== 'viewer' && p.alive;
    this.flameIcon.visible = show;
    if (!show) return;
    const [fx, fy] = moverPos(p, alpha);
    this.flameIcon.position.set(this.cx(fx) + C * 0.6, this.cx(fy) - C * 0.55);
    const l = hud.lantern;
    const level = l ? Math.round((l.fill / l.gemsPerShot) * (ASSETS.lanternMeterLevels - 1)) : 0;
    this.tex(this.flameIcon, ASSETS.lanternMeter(level));
    const ready = !!l && l.fill >= l.gemsPerShot;
    const throb = hud.meterThrob ? 0.05 * Math.abs(Math.sin(performance.now() / 110)) : 0;
    this.flameIcon.scale.set((0.34 + (ready ? 0.03 * Math.sin((state.tick + alpha) * 0.8) : 0) + 0.15 * this.fireFlash + throb) * this.meterPop);
    if (this.fireShake > 0) this.flameIcon.x += Math.sin(performance.now() * 0.06) * C * 0.06 * this.fireShake;
    this.flameIcon.alpha = ready ? 1 : 0.85;
    this.flameIcon.tint = l?.beamReady ? 0xfff0a0 : 0xffffff;
  }

  private syncShadows(state: SimState, alpha: number): void {
    const g = this.shadowG;
    g.clear();
    for (const sk of state.sacks) {
      if (sk.state !== 'fall') continue;
      const ty = sk.y + 1;
      const held = sk.shadow > 0;
      const [px, py] = moverPos(state.player, alpha);
      const onPlayer = Math.round(px) === sk.x && Math.round(py) === ty;
      if (!held && !onPlayer) continue;
      const pulse = 0.55 + 0.35 * Math.sin((state.tick + alpha) * 2.2);
      g.ellipse(this.cx(sk.x), this.cx(ty) + C * 0.3, C * 0.38, C * 0.14).fill({ color: 0x000000, alpha: held ? pulse : 0.35 });
      if (held) g.ellipse(this.cx(sk.x), this.cx(ty) + C * 0.3, C * 0.46, C * 0.18).stroke({ color: PALETTE.flame, width: 3, alpha: pulse });
    }
  }

  /** Gold rings, bonus glow, dimmer and flash overlays. */
  private syncOverlays(state: SimState, alpha: number, hud: HudInfo): void {
    const g = this.goldRingG;
    g.clear();
    for (const gd of state.golds) {
      const frac = Math.max(0, Math.min(1, gd.timer / state.params.goldTicks));
      if (frac >= 0.999) continue;
      const cx = this.cx(gd.x);
      const cy = this.cx(gd.y);
      g.moveTo(cx, cy - C * 0.42).arc(cx, cy, C * 0.42, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * frac).stroke({ color: PALETTE.gold, width: 3, alpha: gd.timer < 30 ? 0.9 : 0.55 });
    }
    const fw = state.width * C;
    const fh = state.height * C;
    this.flash.clear();
    if (state.bonusActive) {
      const pulse = 0.1 + 0.05 * Math.sin((state.tick + alpha) * 0.5);
      this.flash.rect(0, 0, fw, fh).fill({ color: PALETTE.bonus, alpha: pulse });
    }
    if (hud.flashTicks && hud.flashTicks > 0) this.flash.rect(0, 0, fw, fh).fill({ color: PALETTE.bonusGlow, alpha: Math.min(0.35, hud.flashTicks * 0.05) });
    if (this.bonusEndAlpha > 0.005) this.flash.rect(0, 0, fw, fh).fill({ color: PALETTE.bonusGlow, alpha: this.bonusEndAlpha });
    const d = this.dimG;
    d.clear();
    if (this.dimAlpha > 0.005) d.rect(0, 0, fw, fh).fill({ color: PALETTE.dimmer, alpha: this.dimAlpha });
  }

  /** Target panel rows: icon (gem type for typed collects), n/N or seconds left, tick when done. */
  private syncTargetPanel(state: SimState, show: boolean, goals: Goal[]): void {
    this.panel.visible = show;
    this.tex(this.panel, ASSETS.targetPanel);
    this.panel.scale.set(this.panelScale * this.panelPulse);
    this.panelRows.forEach((r, i) => {
      const g = goals[i];
      r.row.visible = show && !!g;
      if (!g) return;
      const icon = g.type === 'collect' && g.gemType >= 0 ? `gem.${g.gemType % PALETTE.gems.length}` : ASSETS.goalIcon(g.type);
      this.tex(r.icon, icon);
      let label: string;
      if (g.type === 'survive') label = g.done ? '✓' : `${Math.ceil(((g.target - g.progress) * DEFAULTS.tickMs) / 1000)}s`;
      else if (g.type === 'rescue') label = g.done ? '✓' : '?';
      else label = g.done ? `${g.target}/${g.target} ✓` : `${g.progress}/${g.target}`;
      if (r.text.text !== label) r.text.text = label;
      r.row.alpha = g.done ? 0.6 : 1;
      r.row.scale.set(this.rowPop[i]);
    });
    void state;
  }

  private syncHud(state: SimState, L: Layout, settings: Settings, hud: HudInfo): void {
    const viewer = hud.mode === 'viewer';
    const now = performance.now();
    // --- Sky band: gradient in 10 strips, drifting clouds, headframe silhouette behind the sign.
    if (this.sceneW !== L.w || this.sceneH !== L.h) {
      this.sceneW = L.w;
      this.sceneH = L.h;
      const g = this.skyG;
      g.clear();
      const bands = 10;
      for (let i = 0; i < bands; i++) {
        const t = i / (bands - 1);
        g.rect(0, (L.topH * i) / bands, L.w, L.topH / bands + 1).fill(lerpColor(PALETTE.skyTop, PALETTE.skyBottom, t));
      }
      const f = this.floorG;
      f.clear();
      f.rect(0, L.floorY, L.w, L.floorH).fill(PALETTE.earthDark);
      f.rect(0, L.floorY, L.w, 6).fill({ color: 0x000000, alpha: 0.25 });
      for (let x = 12; x < L.w; x += 37) f.circle(x, L.floorY + L.floorH * 0.85, 2).fill({ color: PALETTE.earthMid, alpha: 0.6 });
      // Field frame (Bible §2: lacquered wood + brass, mine not castle): shadow below, wooden rail, brass corner bolts.
      const fr = this.frameG;
      fr.clear();
      fr.rect(L.fieldX - 6, L.fieldY + L.fieldH, L.fieldW + 12, 10).fill({ color: 0x000000, alpha: 0.3 });
      fr.roundRect(L.fieldX - 9, L.fieldY - 9, L.fieldW + 18, L.fieldH + 18, 8).stroke({ color: PALETTE.wood, width: 10 });
      fr.roundRect(L.fieldX - 9, L.fieldY - 9, L.fieldW + 18, L.fieldH + 18, 8).stroke({ color: PALETTE.woodLight, width: 3, alpha: 0.5 });
      fr.roundRect(L.fieldX - 3, L.fieldY - 3, L.fieldW + 6, L.fieldH + 6, 4).stroke({ color: 0x000000, width: 3, alpha: 0.35 });
      for (const [bx, by] of [[L.fieldX - 9, L.fieldY - 9], [L.fieldX + L.fieldW + 9, L.fieldY - 9], [L.fieldX - 9, L.fieldY + L.fieldH + 9], [L.fieldX + L.fieldW + 9, L.fieldY + L.fieldH + 9]]) {
        fr.circle(bx, by, 6).fill(PALETTE.brass);
        fr.circle(bx - 1.5, by - 1.5, 2.5).fill(PALETTE.brassLight);
      }
      // Headframe: the sign band carries the HUD; the structure above rises behind it off the top of the screen.
      const signW = Math.min(L.sign.w, L.sign.h * 4.9);
      const signScale = signW / (C * 8);
      this.sign.position.set(L.w / 2, L.sign.y + L.sign.h / 2);
      this.sign.scale.set(signScale, signScale * (L.sign.h / (C * 2.6 * signScale)) );
      this.headframe.position.set(L.w / 2, L.sign.y + L.sign.h * 0.35);
      this.headframe.scale.set((signW * 1.04) / (C * 4));
      this.bust.position.set(L.bust.cx, L.bust.cy);
      this.bust.scale.set((L.bust.r * 2) / C);
      this.plate.position.set(L.plate.x + L.plate.w / 2, L.plate.y + L.plate.h / 2);
      this.plate.scale.set(L.plate.w / (C * 4), (L.plate.h * 1.2) / (C * 1.05));
      this.plateT.position.set(L.plate.x + L.plate.w / 2, L.plate.y + L.plate.h / 2 + 1);
      this.plateT.style.fontSize = Math.round(L.plate.h * 0.62);
      // Score sits on the left plate of the sign art, helmets on the right plate (covering the baked ones).
      const sx0 = L.w / 2 - signW / 2;
      this.signGeom = { sx0, signW };
      // Target panel hangs at the right end of the sign (Systems Brief §1: beside Grandpa).
      const ph = L.sign.h * 1.32;
      const pscale = ph / (C * 3);
      this.panel.position.set(sx0 + signW * 0.855, L.sign.y + L.sign.h * 0.52);
      this.panelScale = pscale;
      this.panel.scale.set(pscale);
      const pw = C * 4 * pscale;
      this.panelRows.forEach((r, i) => {
        r.row.position.set(this.panel.x - pw * 0.5, this.panel.y + (i - 1) * ph * 0.235 + ph * 0.01);
        r.icon.position.set(pw * 0.26, 0);
        r.icon.scale.set((ph * 0.19) / C);
        r.text.position.set(pw * 0.4, 0);
        r.text.style.fontSize = Math.max(9, Math.round(ph * 0.16));
      });
      // Floor props: rails chunks, cart, rope, logs along the mine floor (placeholder rails/cart hidden).
      this.rails.visible = false;
      this.cart.visible = false;
      const fy = L.floorY + L.floorH * 0.86;
      const ps = Math.min(1.1, L.floorH / (C * 2.1));
      const [rails1, rails2, cart, rope, logs] = this.floorProps;
      rails1.position.set(L.w * 0.3, fy + 4);
      rails2.position.set(L.w * 0.66, fy + 4);
      rails1.scale.set(ps * 1.15);
      rails2.scale.set(ps * 1.15);
      cart.position.set(L.w * 0.7, fy - L.floorH * 0.02);
      cart.scale.set(ps * 1.05);
      rope.position.set(L.w * 0.47, fy);
      rope.scale.set(ps * 0.75);
      logs.position.set(L.w * 0.9, fy);
      logs.scale.set(ps * 0.7);
      this.clouds.forEach((c, i) => c.scale.set(0.9 + i * 0.25));
    }
    // Clouds drift.
    this.clouds.forEach((c, i) => {
      const period = 40000 + i * 12000;
      const x = ((now / period + i * 0.33) % 1) * (L.w + 120) - 60;
      c.position.set(x, L.insets.top + 8 + i * (L.topH * 0.16));
      c.alpha = 0.75;
    });
    // --- Sign HUD: score, bust, helmets, brass plate.
    this.tex(this.sign, ASSETS.headframeSign('collapsed'));
    this.tex(this.headframe, ASSETS.headframeTop('collapsed'));
    this.tex(this.plate, ASSETS.brassPlate);
    this.floorProps.forEach((sp, i) => this.tex(sp, [ASSETS.floor.rails, ASSETS.floor.rails, ASSETS.floor.cart, ASSETS.floor.rope, ASSETS.floor.logs][i]));
    this.scoreT.visible = !viewer;
    this.scoreT.text = String(state.score);
    const lives = hud.mode === 'gauntlet' ? DEFAULTS.maxSpareLives : Math.max(0, state.lives);
    // With goals, the panel takes the right plate: score moves up and the helmets tuck under it.
    const goals = this.currentGoals(state);
    const showPanel = goals.length > 0 && !viewer;
    const { sx0, signW } = this.signGeom;
    this.scoreT.position.set(sx0 + signW * 0.085, L.sign.y + L.sign.h * (showPanel ? 0.36 : 0.5));
    this.scoreT.style.fontSize = Math.round(L.sign.h * (showPanel ? 0.34 : 0.42));
    const hr = L.sign.h * (showPanel ? 0.3 : 0.46);
    this.helmets.forEach((h, i) => {
      h.visible = !viewer && i < Math.max(3, lives);
      this.tex(h, i < lives ? ASSETS.helmetLife : 'icon.helmet.empty');
      if (showPanel) h.position.set(sx0 + signW * (0.07 + i * 0.05), L.sign.y + L.sign.h * 0.74);
      else h.position.set(sx0 + signW * (0.905 - i * 0.075), L.sign.y + L.sign.h * 0.5);
      h.scale.set((hr / C) * (i === 0 ? this.livesPop : 1));
      h.alpha = hud.mode === 'gauntlet' ? 0.6 : 1;
    });
    this.syncTargetPanel(state, showPanel, goals);
    // A4: Grandpa's hint line on a small dark plate under the sign, fading in and out.
    const want = hud.grandpaLine && !viewer ? 1 : 0;
    this.speechAlpha += (want - this.speechAlpha) * 0.18;
    if (hud.grandpaLine) this.speechT.text = hud.grandpaLine;
    const sg = this.speechG;
    sg.clear();
    if (this.speechAlpha > 0.02) {
      this.speechT.style.fontSize = Math.round(Math.max(12, L.cell * 0.42));
      const tw = this.speechT.width + L.cell * 0.9;
      const th = this.speechT.height + L.cell * 0.3;
      const sx = L.w / 2;
      const sy = L.plate.y + L.plate.h + th * 0.75 + 4;
      sg.roundRect(sx - tw / 2, sy - th / 2, tw, th, th / 2).fill({ color: 0x1c1410, alpha: 0.88 * this.speechAlpha });
      sg.moveTo(sx - 6, sy - th / 2).lineTo(sx, sy - th / 2 - 7).lineTo(sx + 6, sy - th / 2).fill({ color: 0x1c1410, alpha: 0.88 * this.speechAlpha });
      this.speechT.position.set(sx, sy);
      this.speechT.alpha = this.speechAlpha;
      this.speechT.visible = true;
    } else this.speechT.visible = false;
    this.plateT.text = viewer ? 'BOARD VIEWER' : hud.mode === 'gauntlet' && hud.timeLeftMs !== undefined ? `${Math.max(0, hud.timeLeftMs / 1000).toFixed(1)}s` : hud.levelName;
    // Grandpa: party hat in bonus, snore after 10 s idle, timed reactions, else neutral.
    let bustState: BustState = 'neutral';
    if (state.bonusActive) bustState = 'partyHat';
    else if (this.bustOverride && now < this.bustOverride.until) bustState = this.bustOverride.state;
    else if (hud.idleMs >= SNORE_AFTER_MS && !viewer) bustState = 'snore';
    if (this.bustOverride && now >= this.bustOverride.until) this.bustOverride = null;
    const bustName = bustState === 'snore' ? ASSETS.grandpa.snore(Math.floor(now / 450) % ASSETS.grandpa.snoreFrames) : ASSETS.grandpa[bustState];
    this.tex(this.bust, bustName);
    // Every reaction lands with a small pop of the portrait.
    if (bustState !== this.lastBust) {
      this.lastBust = bustState;
      if (bustState !== 'neutral' && bustState !== 'snore') {
        this.bustPop = 1.14;
        this.tweens.cancel(this.bust);
        this.tweens.add(260, (t) => { this.bustPop = 1.14 - 0.14 * t; }, { ease: Easing.outBack, tag: this.bust });
      }
    }
    const bustRot = bustState === 'snore' ? 0.06 * Math.sin(now / 700) : bustState === 'wince' ? -0.08 : bustState === 'delighted' ? 0.05 * Math.sin(now / 160) : bustState === 'worried' ? 0.03 * Math.sin(now / 60) : 0;
    this.bust.rotation = bustRot;
    this.bust.scale.set(((L.bust.r * 2) / C) * this.bustPop);
    // Bonus timer: the ART4 ring around the portrait steps through its five stages and pulses near the end.
    this.ring.visible = state.bonusActive && !viewer;
    if (this.ring.visible) {
      const frac = state.bonusTimer / Math.max(1, state.params.bonusTicks);
      const stage = ASSETS.timerRingStages - 1 - fillLevel(1 - frac, ASSETS.timerRingStages);
      this.tex(this.ring, ASSETS.timerRing(stage));
      const pulse = frac < 0.25 ? 1 + 0.06 * Math.sin(now / 90) : 1;
      this.ring.position.set(L.bust.cx, L.bust.cy);
      this.ring.scale.set(((L.bust.r * 2 + 16) / C) * pulse);
    }
    // --- Floor: lantern (variant A) on its post, flashing on fire; d-pad if enabled.
    const lan = L.lantern;
    this.lanternS.visible = !!lan && !viewer;
    this.post.visible = !!lan;
    if (lan) {
      this.tex(this.post, ASSETS.floor.lanternPost);
      this.post.anchor.set(0.5, 1);
      this.post.position.set(lan.visual.cx - lan.visual.r * 0.95, lan.visual.cy + lan.visual.r * 1.35);
      this.post.scale.set((lan.visual.r * 2.6) / C);
      const jiggle = this.fireShake > 0 ? Math.sin(now * 0.06) * 3 * this.fireShake : 0;
      const l = hud.lantern;
      const level = l ? Math.round((l.fill / l.gemsPerShot) * (ASSETS.lanternMeterLevels - 1)) : 0;
      this.tex(this.lanternS, ASSETS.lanternMeter(level));
      // The meter is a tall lantern (1×2 cells). It stands beside the post's own lantern, outside
      // its silhouette, base level with the post base, sized to read at arm's length; the hit
      // target stays on the post (the meter is display only).
      this.lanternS.anchor.set(0.5, 1);
      const meterH = lan.visual.r * 4.2;
      this.lanternS.position.set(lan.visual.cx + lan.visual.r * 2.35 + jiggle, lan.visual.cy + lan.visual.r * 1.4);
      const throb = hud.meterThrob ? 0.14 * Math.abs(Math.sin(now / 110)) : 0;
      this.lanternS.scale.set((meterH / (C * 2)) * (1 + 0.12 * this.lanternFlash + 0.25 * this.fireFlash + throb) * this.meterPop);
      this.lanternS.tint = l?.beamReady ? (Math.floor(now / 250) % 2 === 0 ? 0xfff0a0 : 0xffffff) : 0xffffff;
      // Banked charges as small lanterns in a row under the post.
      const n = l?.charges ?? 0;
      this.charges.forEach((c, i) => {
        c.visible = !viewer && i < (l?.maxCharges ?? 0);
        this.tex(c, ASSETS.lanternCharge);
        // Charge pips in a row under the post base, spanning post and meter.
        const r = lan.visual.r * 0.36;
        c.position.set(lan.visual.cx + lan.visual.r * 1.1 + (i - 2) * r * 1.7, lan.visual.cy + lan.visual.r * 1.4 + r * 1.25);
        c.scale.set(((r * 2) / C) * (i === n - 1 ? this.chargePop : 1));
        // The next lantern brightens as gems bank toward it (gemsPerCharge per charge).
        const partial = l && i === n && l.gemsPerCharge > 0 ? l.chargeFill / l.gemsPerCharge : 0;
        c.alpha = i < n ? 1 : 0.28 + 0.45 * partial;
        c.tint = i < n && l?.beamReady ? 0xfff0a0 : 0xffffff;
      });
    }
    else this.charges.forEach((c) => { c.visible = false; });
    this.dpadG.clear();
    if (L.dpad && !viewer) {
      const { cx, cy, size } = L.dpad;
      const b = size / 3;
      const dirs: [Dir, number, number][] = [[1, 0, -1], [2, 1, 0], [3, 0, 1], [4, -1, 0]];
      for (const [d, dx, dy] of dirs) {
        this.dpadG.roundRect(cx + dx * b - b / 2 + 2, cy + dy * b - b / 2 + 2, b - 4, b - 4, 8).fill({ color: hud.dpadHeld === d ? PALETTE.dpadHeld : PALETTE.dpad, alpha: 0.85 });
        const ax = cx + dx * b;
        const ay = cy + dy * b;
        const r = b * 0.22;
        const rot = DIR_ROT[d];
        const pts = [r, 0, -r * 0.6, -r * 0.7, -r * 0.6, r * 0.7];
        const rp: number[] = [];
        for (let i = 0; i < pts.length; i += 2) {
          const x = pts[i];
          const y = pts[i + 1];
          rp.push(ax + x * Math.cos(rot) - y * Math.sin(rot), ay + x * Math.sin(rot) + y * Math.cos(rot));
        }
        this.dpadG.poly(rp).fill(0xbbbbbb);
      }
    }
    void settings;
    // --- Overlays
    this.bonusT.visible = state.bonusActive;
    this.bonusT.text = `BONUS ${state.bonusTimer}`;
    this.bonusT.position.set(L.w / 2, L.floorY + 10);
    const bb = this.bannerBg;
    bb.clear();
    const show = !!hud.banner;
    this.bannerT.visible = this.bannerSubT.visible = show;
    if (show) {
      const cy = L.fieldY + L.fieldH / 2;
      bb.roundRect(L.fieldX, cy - 44, L.fieldW, 88, 14).fill({ color: 0x000000, alpha: 0.65 });
      this.bannerT.style.fontSize = Math.round(L.cell * 0.8);
      this.bannerT.text = hud.banner ?? '';
      this.bannerT.position.set(L.w / 2, cy + (hud.subBanner ? -12 : 0));
      this.bannerSubT.style.fontSize = Math.round(L.cell * 0.45);
      this.bannerSubT.text = hud.subBanner ?? '';
      this.bannerSubT.position.set(L.w / 2, cy + 20);
    }
    this.fpsT2.visible = this.showFps;
    if (this.showFps) {
      this.fpsT2.text = `${this.fps} fps · ${this.particles.count} p · ${this.tweens.active} tw`;
      this.fpsT2.position.set(L.w - 150, L.h - 16);
    }
  }
}

/** Fill level 0..levels-1 from a cooldown fraction (0 = fully recharged → top level). */
function fillLevel(fireFrac: number, levels: number): number {
  return Math.max(0, Math.min(levels - 1, Math.floor((1 - fireFrac) * (levels - 1) + 0.999)));
}

function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255, ag = (a >> 8) & 255, ab = a & 255;
  const br = (b >> 16) & 255, bg = (b >> 8) & 255, bb = b & 255;
  return (Math.round(ar + (br - ar) * t) << 16) | (Math.round(ag + (bg - ag) * t) << 8) | Math.round(ab + (bb - ab) * t);
}
